import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  createSchemaTactic,
  deleteSchemaTactic,
  fetchSchemaTactics,
  fetchVolleySchemas,
  isSchemasEnabled,
  migrateSchemaTacticsFromSchemas,
  migrateVolleySchemasToRows,
  SchemaTactic,
  trackActivityEvent,
  updateSchemaTactic,
  upsertVolleySchemas,
} from "../api";

type PlayerKey = "Pass" | "Poi" | "Lib" | "R4" | "C1" | "C2";

// Sélection d'un poste côté lecture/animation (ne touche pas au schéma sauvegardé)
type MyRoleKey = PlayerKey | "R4_1" | "R4_2" | SubstituteKey;

type SubstituteKey = "Sub_Pass" | "Sub_Poi" | "Sub_Lib" | "Sub_R4" | "Sub_C1" | "Sub_C2";

type Pos = { nx: number; ny: number };

type BallPos = { nx: number; ny: number };

type Positions = Partial<Record<PlayerKey, Pos>>;

type Substitutes = Partial<Record<SubstituteKey, Pos>>;

type RoleChange = { from: PlayerKey; to: PlayerKey };

type RoleMap = Partial<Record<PlayerKey, PlayerKey>>;

type Step = {
  label: string;
  positions: Positions;
  roles?: RoleMap;
  substitutes?: Substitutes;
  ball?: BallPos;
  roleChanges?: RoleChange[];
  // Étape sur laquelle porte le quiz pour ce schéma (une seule par schéma).
  // Si aucune étape n'est marquée, le quiz retombe sur l'« Étape 1 ».
  quiz?: boolean;
};

type SchemaType = "service" | "reception";

type Schema = {
  id: string;
  name: string;
  type: SchemaType;
  playerCount: 4 | 6;
  tactic?: string;
  tacticId?: number | null;
  createdAt: number;
  base: Positions;
  perm: Positions;
  steps?: Step[];
};

type VolleySchemaStudioProps = {
  isAdmin: boolean;
};

const PC: Record<PlayerKey, { bg: string; label: string }> = {
  Pass: { bg: "#ef4444", label: "Passeuse" },
  Poi: { bg: "#8b5cf6", label: "Pointue" },
  Lib: { bg: "#1a1a1a", label: "Libéro" },
  R4: { bg: "#f59e0b", label: "R4" },
  C1: { bg: "#3b82f6", label: "Centrale" },
  C2: { bg: "#ec4899", label: "Centrale" },
};

const SUB_PC: Record<SubstituteKey, { bg: string; label: string; baseRole: PlayerKey }> = {
  Sub_Pass: { bg: "#ef4444", label: "Pass (Banc)", baseRole: "Pass" },
  Sub_Poi: { bg: "#8b5cf6", label: "Poi (Banc)", baseRole: "Poi" },
  Sub_Lib: { bg: "#1a1a1a", label: "Lib (Banc)", baseRole: "Lib" },
  Sub_R4: { bg: "#f59e0b", label: "R4 (Banc)", baseRole: "R4" },
  Sub_C1: { bg: "#3b82f6", label: "C (Banc)", baseRole: "C1" },
  Sub_C2: { bg: "#ec4899", label: "C (Banc)", baseRole: "C2" },
};

const shortRoleCode = (role: PlayerKey): string => {
  if (role === "C1" || role === "C2") return "C";
  return role;
};

/** Étiquette contextuelle : "R4 1" en zone avant, "R4" en zone arrière, "C" pour les centrales. */
const zoneRoleLabel = (role: PlayerKey, ny: number): string => {
  if (role === "C1" || role === "C2") return "C";
  if (role === "Poi") return "Poi";
  if (role === "R4") {
    const isAvant = ny > MY + LH * 0.12 && ny < MY + LH * 0.42;
    return isAvant ? "R4 1" : "R4 2";
  }
  return shortRoleCode(role);
};

const isR4Instance = (r: MyRoleKey): r is "R4_1" | "R4_2" => r === "R4_1" || r === "R4_2";

const isSubstituteRole = (r: MyRoleKey): r is SubstituteKey => typeof r === "string" && r.startsWith("Sub_");

const baseRoleOfMyRole = (r: MyRoleKey): PlayerKey => {
  if (isR4Instance(r)) return "R4";
  if (isSubstituteRole(r)) return SUB_PC[r].baseRole;
  return r;
};

const P6: PlayerKey[] = ["Pass", "Poi", "Lib", "R4", "C1", "C2"];
const P4: PlayerKey[] = ["Pass", "Poi", "R4", "C1"];

const SUB_6: SubstituteKey[] = ["Sub_Pass", "Sub_Poi", "Sub_Lib", "Sub_R4", "Sub_C1", "Sub_C2"];
const SUB_4: SubstituteKey[] = ["Sub_Pass", "Sub_Poi", "Sub_R4", "Sub_C1"];

const MX = 0.11,
  MY = 0.08,
  LW = 1 - MX * 2,
  LH = 1 - MY * 2;

// Zone du banc en bas du terrain
const BENCH_Y = 1 - MY / 2; // Position Y du banc (en bas, hors terrain)
const BENCH_START_X = MX + LW * 0.15; // Début de la zone banc
const BENCH_SPACING_X = LW * 0.12; // Espacement horizontal entre les remplaçants

const cm = (nx: number, ny: number): Pos => ({ nx: MX + nx * LW, ny: MY + ny * LH });
const eioSmooth = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const eioIntro = (t: number) => {
  const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  return eased + Math.sin(Math.PI * eased) * 0.035;
};
const gap = (a: Pos, b: Pos) => Math.sqrt((a.nx - b.nx) ** 2 + (a.ny - b.ny) ** 2);

const getZone = (pos: Pos): string => {
  const zNum =
    pos.ny < 0.45
      ? pos.nx < 0.37
        ? "Z4"
        : pos.nx > 0.63
          ? "Z2"
          : "Z3"
      : pos.nx < 0.37
        ? "Z5"
        : pos.nx > 0.63
          ? "Z1"
          : "Z6";
  return zNum;
};

const PRE_IDS = new Set<string>();

const PRE_BASE: Array<Omit<Schema, "steps">> = [
  {
    id: "ps1",
    name: "P1 — Service",
    type: "service",
    playerCount: 6,
    createdAt: 1e9 + 1,
    base: {
      C1: cm(0.18, 0.28),
      C2: cm(0.5, 0.28),
      Poi: cm(0.82, 0.28),
      Lib: cm(0.18, 0.72),
      R4: cm(0.5, 0.72),
      Pass: cm(0.9, 0.96),
    },
    perm: {
      C1: cm(0.18, 0.22),
      C2: cm(0.5, 0.22),
      Pass: cm(0.82, 0.22),
      Lib: cm(0.18, 0.68),
      R4: cm(0.5, 0.68),
      Poi: cm(0.82, 0.78),
    },
  },
  {
    id: "pr1",
    name: "P1 — Réception",
    type: "reception",
    playerCount: 6,
    createdAt: 1e9 + 2,
    base: {
      C1: cm(0.18, 0.28),
      C2: cm(0.5, 0.28),
      Poi: cm(0.82, 0.28),
      Lib: cm(0.18, 0.72),
      R4: cm(0.5, 0.72),
      Pass: cm(0.9, 0.96),
    },
    perm: {
      C1: cm(0.18, 0.22),
      C2: cm(0.5, 0.22),
      Pass: cm(0.82, 0.22),
      Lib: cm(0.18, 0.68),
      R4: cm(0.5, 0.68),
      Poi: cm(0.82, 0.78),
    },
  },
  {
    id: "ps2",
    name: "P2 — Service",
    type: "service",
    playerCount: 6,
    createdAt: 1e9 + 3,
    base: {
      C1: cm(0.18, 0.28),
      C2: cm(0.5, 0.28),
      Pass: cm(0.82, 0.28),
      Lib: cm(0.18, 0.72),
      R4: cm(0.5, 0.72),
      Poi: cm(0.9, 0.96),
    },
    perm: {
      C2: cm(0.18, 0.22),
      C1: cm(0.5, 0.22),
      Pass: cm(0.82, 0.22),
      R4: cm(0.18, 0.68),
      Lib: cm(0.5, 0.68),
      Poi: cm(0.82, 0.78),
    },
  },
  {
    id: "pr2",
    name: "P2 — Réception",
    type: "reception",
    playerCount: 6,
    createdAt: 1e9 + 4,
    base: {
      C1: cm(0.18, 0.28),
      C2: cm(0.5, 0.28),
      Pass: cm(0.82, 0.28),
      Lib: cm(0.18, 0.72),
      R4: cm(0.5, 0.72),
      Poi: cm(0.9, 0.96),
    },
    perm: {
      C2: cm(0.18, 0.22),
      C1: cm(0.5, 0.22),
      Pass: cm(0.82, 0.22),
      R4: cm(0.18, 0.68),
      Lib: cm(0.5, 0.68),
      Poi: cm(0.82, 0.78),
    },
  },
  {
    id: "ps3",
    name: "P3 — Service",
    type: "service",
    playerCount: 6,
    createdAt: 1e9 + 5,
    base: {
      Poi: cm(0.18, 0.28),
      Pass: cm(0.5, 0.28),
      C1: cm(0.82, 0.28),
      C2: cm(0.18, 0.72),
      Lib: cm(0.5, 0.72),
      R4: cm(0.9, 0.96),
    },
    perm: {
      Poi: cm(0.18, 0.22),
      C1: cm(0.5, 0.22),
      Pass: cm(0.82, 0.22),
      C2: cm(0.18, 0.68),
      Lib: cm(0.5, 0.72),
      R4: cm(0.76, 0.72),
    },
  },
  {
    id: "pr3",
    name: "P3 — Réception",
    type: "reception",
    playerCount: 6,
    createdAt: 1e9 + 6,
    base: {
      Poi: cm(0.18, 0.28),
      Pass: cm(0.5, 0.28),
      C1: cm(0.82, 0.28),
      C2: cm(0.18, 0.72),
      Lib: cm(0.5, 0.72),
      R4: cm(0.9, 0.96),
    },
    perm: {
      Poi: cm(0.18, 0.22),
      C1: cm(0.5, 0.22),
      Pass: cm(0.82, 0.22),
      C2: cm(0.18, 0.68),
      Lib: cm(0.5, 0.72),
      R4: cm(0.76, 0.72),
    },
  },
  {
    id: "ps4",
    name: "P4 — Service",
    type: "service",
    playerCount: 6,
    createdAt: 1e9 + 7,
    base: {
      Pass: cm(0.18, 0.28),
      C1: cm(0.5, 0.28),
      R4: cm(0.82, 0.28),
      Poi: cm(0.18, 0.72),
      C2: cm(0.5, 0.72),
      Lib: cm(0.9, 0.96),
    },
    perm: {
      R4: cm(0.18, 0.22),
      C1: cm(0.5, 0.22),
      Pass: cm(0.82, 0.22),
      Poi: cm(0.18, 0.68),
      C2: cm(0.5, 0.68),
      Lib: cm(0.78, 0.72),
    },
  },
  {
    id: "pr4",
    name: "P4 — Réception",
    type: "reception",
    playerCount: 6,
    createdAt: 1e9 + 8,
    base: {
      Pass: cm(0.18, 0.28),
      C1: cm(0.5, 0.28),
      R4: cm(0.82, 0.28),
      Poi: cm(0.18, 0.72),
      C2: cm(0.5, 0.72),
      Lib: cm(0.9, 0.96),
    },
    perm: {
      R4: cm(0.18, 0.22),
      C1: cm(0.5, 0.22),
      Pass: cm(0.82, 0.22),
      Poi: cm(0.18, 0.68),
      C2: cm(0.5, 0.68),
      Lib: cm(0.78, 0.72),
    },
  },
  {
    id: "ps5",
    name: "P5 — Service",
    type: "service",
    playerCount: 6,
    createdAt: 1e9 + 9,
    base: {
      R4: cm(0.18, 0.28),
      C1: cm(0.5, 0.28),
      C2: cm(0.82, 0.28),
      Pass: cm(0.18, 0.72),
      Poi: cm(0.5, 0.72),
      Lib: cm(0.9, 0.96),
    },
    perm: {
      C1: cm(0.18, 0.22),
      R4: cm(0.5, 0.22),
      C2: cm(0.82, 0.22),
      Pass: cm(0.82, 0.35),
      Poi: cm(0.5, 0.68),
      Lib: cm(0.25, 0.72),
    },
  },
  {
    id: "pr5",
    name: "P5 — Réception",
    type: "reception",
    playerCount: 6,
    createdAt: 1e9 + 10,
    base: {
      R4: cm(0.18, 0.28),
      C1: cm(0.5, 0.28),
      C2: cm(0.82, 0.28),
      Pass: cm(0.18, 0.72),
      Poi: cm(0.5, 0.72),
      Lib: cm(0.9, 0.96),
    },
    perm: {
      C1: cm(0.18, 0.22),
      R4: cm(0.5, 0.22),
      C2: cm(0.82, 0.22),
      Pass: cm(0.82, 0.35),
      Poi: cm(0.5, 0.68),
      Lib: cm(0.25, 0.72),
    },
  },
  {
    id: "ps6",
    name: "P6 — Service",
    type: "service",
    playerCount: 6,
    createdAt: 1e9 + 11,
    base: {
      C2: cm(0.18, 0.28),
      R4: cm(0.5, 0.28),
      C1: cm(0.82, 0.28),
      Lib: cm(0.18, 0.72),
      Pass: cm(0.5, 0.72),
      Poi: cm(0.9, 0.96),
    },
    perm: {
      C2: cm(0.18, 0.22),
      R4: cm(0.5, 0.22),
      C1: cm(0.82, 0.22),
      Lib: cm(0.2, 0.68),
      Pass: cm(0.82, 0.28),
      Poi: cm(0.82, 0.78),
    },
  },
  {
    id: "pr6",
    name: "P6 — Réception",
    type: "reception",
    playerCount: 6,
    createdAt: 1e9 + 12,
    base: {
      C2: cm(0.18, 0.28),
      R4: cm(0.5, 0.28),
      C1: cm(0.82, 0.28),
      Lib: cm(0.18, 0.72),
      Pass: cm(0.5, 0.72),
      Poi: cm(0.9, 0.96),
    },
    perm: {
      C2: cm(0.18, 0.22),
      R4: cm(0.5, 0.22),
      C1: cm(0.82, 0.22),
      Lib: cm(0.2, 0.68),
      Pass: cm(0.82, 0.28),
      Poi: cm(0.82, 0.78),
    },
  },
];

const PRE: Schema[] = PRE_BASE.map((p) => ({
  ...p,
  steps: [
    { label: "Réglementaire", positions: p.base },
    { label: "Jeu", positions: p.perm },
  ],
}));

PRE.forEach((p) => PRE_IDS.add(p.id));



function getSchemasScope(): string {
  // Scope unique : coachs et joueuses lisent/écrivent le même espace.
  // (L'ancien scope par équipe rendait les modifs du coach invisibles
  // pour les joueuses, qui n'ont jamais de "selectedTeam".)
  return "global";
}

function uniqById(list: Schema[]): Schema[] {
  const map = new Map<string, Schema>();
  list.forEach((s) => {
    if (!s?.id) return;
    // Ne pas écraser : la liste est triée DESC par updated_at,
    // donc le premier occurrence est la plus récente → on la garde.
    if (!map.has(s.id)) map.set(s.id, s);
  });
  return Array.from(map.values());
}

function coercePlayerCount(v: unknown): 4 | 6 {
  return Number(v) === 4 ? 4 : 6;
}

function normalizeSchemas(list: unknown): Schema[] {
  if (!Array.isArray(list)) return [];
  return list
    .filter((s): s is any => !!s && typeof s === "object")
    .map((s) => ({
      ...s,
      // Important: certains schémas importés/anciens peuvent avoir playerCount en string ("6" / "4")
      playerCount: coercePlayerCount((s as any).playerCount),
      tactic: typeof (s as any).tactic === "string" ? (s as any).tactic : "",
      tacticId: Number.isFinite(Number((s as any).tacticId)) ? Number((s as any).tacticId) : null,
    })) as Schema[];
}



function sortSchemasForList(list: Schema[], tacticById?: Map<number, string>): Schema[] {
  return [...list].sort((a, b) => {
    const ta = String((a.tacticId != null ? tacticById?.get(a.tacticId) : a.tactic) || "")
      .trim()
      .toLowerCase();
    const tb = String((b.tacticId != null ? tacticById?.get(b.tacticId) : b.tactic) || "")
      .trim()
      .toLowerCase();
    if (ta !== tb) return ta.localeCompare(tb, "fr");
    return (a.name || "").localeCompare(b.name || "", "fr");
  });
}

function exportJSON(schemas: Schema[]) {
  const json = JSON.stringify(schemas, null, 2);
  try {
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `volley_schemas_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    const uri = "data:application/json;charset=utf-8," + encodeURIComponent(json);
    const a = document.createElement("a");
    a.href = uri;
    a.download = `volley_schemas_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

function importJSON(file: File, onDone: (schemas: Schema[]) => void) {
  const r = new FileReader();
  r.onload = (e) => {
    try {
      const data = JSON.parse(String(e.target?.result ?? "null")) as unknown;
      if (!Array.isArray(data)) throw new Error("Format invalide");
      onDone(data as Schema[]);
    } catch (err) {
      alert("Fichier JSON invalide : " + (err as Error).message);
    }
  };
  r.readAsText(file);
}

function Court({ w, h, children }: { w: number; h: number; children?: React.ReactNode }) {
  const lx = MX * w,
    ly = MY * h,
    lw = LW * w,
    lh = LH * h;
  const netY = ly + lh * 0.12;
  const m3Y = ly + lh * 0.42;
  const zAv = (netY + m3Y) / 2;
  const zArr = (m3Y + ly + lh) / 2;
  const zones: Array<[string, number, number]> = [
    ["Z4", lx + lw * 0.167, zAv],
    ["Z3", lx + lw * 0.5, zAv],
    ["Z2", lx + lw * 0.833, zAv],
    ["Z5", lx + lw * 0.167, zArr],
    ["Z6", lx + lw * 0.5, zArr],
    ["Z1", lx + lw * 0.833, zArr],
  ];

  return (
    <svg width={w} height={h} style={{ display: "block", userSelect: "none", touchAction: "none" }}>
      <rect x={0} y={0} width={w} height={h} rx={8} fill="#0b2e4a" />
      <rect x={lx} y={ly} width={lw} height={lh} fill="#1565a8" />

      {/* Zones avant / arrière */}
      <rect x={lx} y={netY} width={lw} height={m3Y - netY} fill="rgba(255,140,30,.35)" stroke="rgba(255,130,0,.7)" strokeWidth={1.5} />
      <rect x={lx} y={m3Y} width={lw} height={ly + lh - m3Y} fill="rgba(0,200,170,.30)" stroke="rgba(0,200,170,.65)" strokeWidth={1.5} />

      <rect x={lx} y={ly} width={lw} height={netY - ly + lh * 0.08} fill="rgba(0,0,0,.18)" rx={2} />

      {[1 / 3, 2 / 3].map((r, i) => (
        <line
          key={i}
          x1={lx + r * lw}
          y1={ly + 1}
          x2={lx + r * lw}
          y2={ly + lh - 1}
          stroke="rgba(255,255,255,.13)"
          strokeWidth={1}
        />
      ))}

      <line
        x1={lx}
        y1={m3Y}
        x2={lx + lw}
        y2={m3Y}
        stroke="rgba(255,255,255,.55)"
        strokeWidth={1.5}
        strokeDasharray="6,4"
      />
      <text
        x={lx - 5}
        y={m3Y}
        textAnchor="end"
        dominantBaseline="central"
        fontSize={7}
        fill="rgba(255,255,255,.45)"
        fontFamily="sans-serif"
      >
        3m
      </text>

      <rect x={lx} y={ly} width={lw} height={lh} fill="none" stroke="rgba(255,255,255,.55)" strokeWidth={2} />
      <rect x={0.5} y={0.5} width={w - 1} height={h - 1} rx={7.5} fill="none" stroke="rgba(255,255,255,.07)" strokeWidth={1} />

      <rect x={lx - 6} y={netY - 12} width={lw + 12} height={24} rx={2} fill="rgba(0,0,0,.2)" />

      {Array.from({ length: Math.floor(lw / 10) }).map((_, i) => (
        <line
          key={i}
          x1={lx + i * 10 + 5}
          y1={netY - 10}
          x2={lx + i * 10 + 5}
          y2={netY + 10}
          stroke="rgba(255,255,255,.08)"
          strokeWidth={0.7}
        />
      ))}

      <line x1={lx - 6} y1={netY - 6} x2={lx + lw + 6} y2={netY - 6} stroke="rgba(255,255,255,.2)" strokeWidth={1} />
      <line x1={lx - 6} y1={netY} x2={lx + lw + 6} y2={netY} stroke="rgba(255,255,255,.2)" strokeWidth={1} />
      <line x1={lx - 6} y1={netY + 6} x2={lx + lw + 6} y2={netY + 6} stroke="rgba(255,255,255,.2)" strokeWidth={1} />

      <rect x={lx - 6} y={netY - 11} width={lw + 12} height={5} rx={1} fill="rgba(255,255,255,.75)" />

      <line x1={lx} y1={netY - 18} x2={lx} y2={netY + 18} stroke="rgba(255,255,255,.65)" strokeWidth={3.5} strokeLinecap="round" />
      <line
        x1={lx + lw}
        y1={netY - 18}
        x2={lx + lw}
        y2={netY + 18}
        stroke="rgba(255,255,255,.65)"
        strokeWidth={3.5}
        strokeLinecap="round"
      />

      {[lx, lx + lw].map((ax, ai) => (
        <g key={ai}>
          {[0, 1, 2, 3, 4, 5].map((s) => (
            <rect
              key={s}
              x={ax - 1.5}
              y={netY - 20 + s * 5}
              width={3}
              height={4}
              rx={0.5}
              fill={s % 2 === 0 ? "#ef4444" : "#fff"}
              opacity={0.9}
            />
          ))}
        </g>
      ))}

      {zones.map(([z, zx, zy]) => (
        <g key={z}>
          <rect x={zx - 13} y={zy - 8} width={26} height={16} rx={8} fill="rgba(255,255,255,.08)" stroke="rgba(255,255,255,.14)" strokeWidth={0.8} />
          <text
            x={zx}
            y={zy}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={8.5}
            fill="rgba(255,255,255,.45)"
            fontFamily="monospace"
            fontWeight={700}
          >
            {z}
          </text>
        </g>
      ))}

      <text
        x={lx / 2}
        y={h / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={6}
        fill="rgba(255,255,255,.14)"
        fontFamily="monospace"
        transform={`rotate(-90,${lx / 2},${h / 2})`}
      >
        hors ligne
      </text>
      <text
        x={lx + lw + lx / 2}
        y={h / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={6}
        fill="rgba(255,255,255,.14)"
        fontFamily="monospace"
        transform={`rotate(90,${lx + lw + lx / 2},${h / 2})`}
      >
        hors ligne
      </text>

      {/* Zone du banc en bas */}
      <g>
        <rect
          x={lx}
          y={ly + lh + 10}
          width={lw}
          height={ly - 20}
          rx={6}
          fill="rgba(255,255,255,.05)"
          stroke="rgba(255,255,255,.15)"
          strokeWidth={1}
          strokeDasharray="4,3"
        />
        <text
          x={lx + lw / 2}
          y={ly + lh + ly / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={8}
          fill="rgba(255,255,255,.35)"
          fontFamily="sans-serif"
          fontWeight={700}
        >
          BANC
        </text>
      </g>

      {children}
    </svg>
  );
}

function DragP({
  id,
  role,
  nx,
  ny,
  zoneNy,
  w,
  h,
  onMove,
  sel,
  onSel,
  onRoleChange,
  usedRoles,
  existingRoles,
}: {
  id: PlayerKey;
  role?: PlayerKey;
  nx: number;
  ny: number;
  zoneNy?: number;
  w: number;
  h: number;
  onMove: (id: PlayerKey, pos: Pos) => void;
  sel: boolean;
  onSel: (id: PlayerKey) => void;
  onRoleChange?: (playerId: PlayerKey, newRole: PlayerKey) => void;
  usedRoles?: PlayerKey[];
  existingRoles?: PlayerKey[];
}) {
  const curRole = role ?? id;
  const c = PC[curRole];
  const x = nx * w,
    y = ny * h,
    R = 18;
  const drag = useRef(false);
  const isLibero = curRole === "Lib";
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });

  // Lueur de zone avant (orange vif) / arrière (acqua) — seulement si sur le terrain
  const zRef = zoneNy ?? ny;
  const isAvant = zRef > MY + LH * 0.12 && zRef < MY + LH * 0.42;
  const isArriere = zRef >= MY + LH * 0.42 && zRef <= MY + LH;
  const zoneGlowColor = isAvant ? "#ff8800" : isArriere ? "#00d4b4" : null;

  const handleRightClick = useCallback(
    (e: React.MouseEvent<SVGGElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (onRoleChange) {
        // Position du menu en coordonnées d'écran
        setMenuPos({ x: e.clientX, y: e.clientY });
        setShowMenu(true);
      }
    },
    [onRoleChange]
  );

  // Fermer le menu quand on clique ailleurs
  useEffect(() => {
    if (!showMenu) return;
    const handler = () => setShowMenu(false);
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, [showMenu]);

  const down = useCallback(
    (e: React.MouseEvent<SVGGElement> | React.TouchEvent<SVGGElement>) => {
      e.stopPropagation();
      e.preventDefault();
      drag.current = true;
      onSel(id);

      const svg = (e.currentTarget as SVGGElement).closest("svg") as SVGSVGElement | null;
      if (!svg) return;

      const gp = (ev: MouseEvent | TouchEvent) => {
        const r = svg.getBoundingClientRect();
        const touch = "touches" in ev ? ev.touches[0] : undefined;
        const cx = touch ? touch.clientX : (ev as MouseEvent).clientX;
        const cy = touch ? touch.clientY : (ev as MouseEvent).clientY;
        return {
          nx: Math.max(0.01, Math.min(0.99, (cx - r.left) / r.width)),
          ny: Math.max(0.01, Math.min(0.99, (cy - r.top) / r.height)),
        };
      };

      const mv = (ev: MouseEvent | TouchEvent) => {
        if (!drag.current) return;
        onMove(id, gp(ev));
      };

      const up = () => {
        drag.current = false;
        window.removeEventListener("mousemove", mv);
        window.removeEventListener("mouseup", up);
        window.removeEventListener("touchmove", mv);
        window.removeEventListener("touchend", up);
      };

      window.addEventListener("mousemove", mv);
      window.addEventListener("mouseup", up);
      window.addEventListener("touchmove", mv, { passive: false });
      window.addEventListener("touchend", up);
    },
    [id, onMove, onSel]
  );

  return (
    <>
      <g style={{ cursor: "grab" }} onMouseDown={down} onTouchStart={down} onContextMenu={handleRightClick}>
        {zoneGlowColor && <circle cx={x} cy={y} r={R + 11} fill={zoneGlowColor} opacity={0.55} />}
        {sel && <circle cx={x} cy={y} r={R + 7} fill={c.bg} opacity={0.25} />}
        {isLibero && <circle cx={x} cy={y} r={R + 5} fill="none" stroke="#10b981" strokeWidth={2.5} opacity={0.8} />}
        <circle cx={x} cy={y} r={R} fill={c.bg} stroke={sel ? "#fff" : isLibero ? "#10b981" : "rgba(255,255,255,.4)"} strokeWidth={sel ? 2.5 : isLibero ? 2.5 : 1.5} />
        {(() => { const lbl = zoneRoleLabel(curRole, zRef); return (
          <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={lbl.length > 3 ? 7.5 : 8.5} fontWeight={700} fill="white" fontFamily="monospace">
            {lbl}
          </text>
        ); })()}
      </g>
      {showMenu && createPortal(
        <div
          style={{
            position: "fixed",
            left: menuPos.x,
            top: menuPos.y,
            zIndex: 9999,
            background: "#fff",
            border: "2px solid #3b82f6",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,.15)",
            padding: "8px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            minWidth: 180,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", marginBottom: 4 }}>
            Changer {shortRoleCode(curRole)} en :
          </div>
          {(["Pass", "Poi", "Lib", "R4", "C1", "C2"] as PlayerKey[])
            .filter((r) => r !== curRole)
            .map((r) => {
              const alreadyUsed = (usedRoles ?? existingRoles ?? []).includes(r);
              return (
                <button
                  key={r}
                  onClick={() => {
                    if (onRoleChange) {
                      onRoleChange(id, r);
                    }
                    setShowMenu(false);
                  }}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: `1.5px solid ${alreadyUsed ? "#f59e0b" : "#e5e7eb"}`,
                    background: alreadyUsed ? "#fef3c7" : PC[r].bg + "15",
                    color: "#111827",
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: PC[r].bg,
                      flexShrink: 0,
                    }}
                  />
                        {shortRoleCode(r)} - {PC[r].label}
                  {alreadyUsed && (
                    <span style={{ marginLeft: "auto", fontSize: 10, color: "#f59e0b" }}>
                      ⚠️
                    </span>
                  )}
                </button>
              );
            })}
          <button
            onClick={() => setShowMenu(false)}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              color: "#6b7280",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              marginTop: 4,
            }}
          >
            Annuler
          </button>
        </div>,
        document.body
      )}
    </>
  );
}

function StatP({
  id,
  role,
  text,
  nx,
  ny,
  w,
  h,
  glow,
  spotlight,
  dimmed,
  focusProg,
  focusDimOthers,
  transitionFromRole,
  transitionToRole,
  transitionT,
  zoneNy,
}: {
  id: PlayerKey;
  role?: PlayerKey;
  text?: string;
  nx: number;
  ny: number;
  w: number;
  h: number;
  glow?: boolean;
  spotlight?: boolean;
  dimmed?: boolean;
  focusProg?: number;
  focusDimOthers?: boolean;
  transitionFromRole?: PlayerKey;
  transitionToRole?: PlayerKey;
  transitionT?: number;
  zoneNy?: number;
}) {
  const curRole = role ?? id;
  const c = PC[curRole];
  const x = nx * w,
    y = ny * h,
    R = 18;
  const baseOpacity = dimmed ? 0.25 : 1;
  const isLibero = curRole === "Lib";

  // Lueur de zone : basée sur la position de référence (étape réglementaire)
  const zRef = zoneNy ?? ny;
  const isAvant = zRef > MY + LH * 0.12 && zRef < MY + LH * 0.42;
  const isArriere = zRef >= MY + LH * 0.42 && zRef <= MY + LH;
  const zoneGlowColor = isAvant ? "#ff8800" : isArriere ? "#00d4b4" : null;

  const hasFocus = typeof focusProg === "number";
  const t = hasFocus ? Math.max(0, Math.min(1, focusProg as number)) : 0;
  const tri = t < 0.5 ? t / 0.5 : (1 - t) / 0.5; // 0 -> 1 -> 0
  const scale = hasFocus ? 1 + 0.55 * tri : 1;
  const fade = hasFocus ? 1 - 0.65 * tri : 1;
  const opacity = (focusDimOthers ? 0.18 : baseOpacity) * fade;
  const transform = hasFocus ? `translate(${x} ${y}) scale(${scale}) translate(${-x} ${-y})` : undefined;

  const hasTransition =
    transitionFromRole &&
    transitionToRole &&
    typeof transitionT === "number" &&
    transitionFromRole !== transitionToRole;
  const tt = hasTransition ? Math.max(0, Math.min(1, transitionT as number)) : 0;
  
  return (
    <g opacity={opacity} transform={transform}>
      {spotlight && <circle cx={x} cy={y} r={R + 12} fill={c.bg} opacity={0.18} />}
      {spotlight && <circle cx={x} cy={y} r={R + 7} fill="none" stroke={c.bg} strokeWidth={2} strokeDasharray="3,3" opacity={0.7} />}

      {!hasTransition ? (
        <>
          {zoneGlowColor && <circle cx={x} cy={y} r={R + 11} fill={zoneGlowColor} opacity={0.55} />}
          {glow && <circle cx={x} cy={y} r={R + 8} fill={c.bg} opacity={0.22} stroke="#fff" strokeWidth={1} strokeDasharray="3,2" />}
          {isLibero && <circle cx={x} cy={y} r={R + 5} fill="none" stroke="#10b981" strokeWidth={2.5} opacity={0.8} />}
          <circle
            cx={x}
            cy={y}
            r={R}
            fill={c.bg}
            stroke={spotlight ? "#fff" : glow ? "#fff" : isLibero ? "#10b981" : "rgba(255,255,255,.35)"}
            strokeWidth={spotlight ? 3 : glow ? 2.5 : isLibero ? 2.5 : 1.5}
          />
          {(() => { const lbl = zoneRoleLabel(curRole, zRef); return (
            <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={lbl.length > 3 ? 7.5 : 8.5} fontWeight={700} fill="white" fontFamily="monospace">
              {lbl}
            </text>
          ); })()}
        </>
      ) : (
        (() => {
          const fr = transitionFromRole as PlayerKey;
          const tr = transitionToRole as PlayerKey;
          const cFrom = PC[fr];
          const cTo = PC[tr];
          const isLibFrom = fr === "Lib";
          const isLibTo = tr === "Lib";
          const oFrom = 1 - tt;
          const oTo = tt;

          return (
            <>
              {zoneGlowColor && <circle cx={x} cy={y} r={R + 11} fill={zoneGlowColor} opacity={0.55} />}
              {glow && <circle cx={x} cy={y} r={R + 8} fill={cFrom.bg} opacity={0.16 * oFrom} stroke="#fff" strokeWidth={1} strokeDasharray="3,2" />}
              {glow && <circle cx={x} cy={y} r={R + 8} fill={cTo.bg} opacity={0.16 * oTo} stroke="#fff" strokeWidth={1} strokeDasharray="3,2" />}

              {isLibFrom && <circle cx={x} cy={y} r={R + 5} fill="none" stroke="#10b981" strokeWidth={2.5} opacity={0.8 * oFrom} />}
              {isLibTo && <circle cx={x} cy={y} r={R + 5} fill="none" stroke="#10b981" strokeWidth={2.5} opacity={0.8 * oTo} />}

              <circle
                cx={x}
                cy={y}
                r={R}
                fill={cFrom.bg}
                opacity={oFrom}
                stroke={spotlight ? "#fff" : glow ? "#fff" : isLibFrom ? "#10b981" : "rgba(255,255,255,.35)"}
                strokeWidth={spotlight ? 3 : glow ? 2.5 : isLibFrom ? 2.5 : 1.5}
              />
              <circle
                cx={x}
                cy={y}
                r={R}
                fill={cTo.bg}
                opacity={oTo}
                stroke={spotlight ? "#fff" : glow ? "#fff" : isLibTo ? "#10b981" : "rgba(255,255,255,.35)"}
                strokeWidth={spotlight ? 3 : glow ? 2.5 : isLibTo ? 2.5 : 1.5}
              />

              {(() => { const lblF = zoneRoleLabel(fr, zRef); return (
                <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={lblF.length > 3 ? 7.5 : 8.5} fontWeight={700} fill="white" fontFamily="monospace" opacity={oFrom}>
                  {lblF}
                </text>
              ); })()}
              {(() => { const lblT = zoneRoleLabel(tr, zRef); return (
                <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={lblT.length > 3 ? 7.5 : 8.5} fontWeight={700} fill="white" fontFamily="monospace" opacity={oTo}>
                  {lblT}
                </text>
              ); })()}
            </>
          );
        })()
      )}

      {spotlight && <circle cx={x} cy={y} r={R + 14} fill="none" stroke={c.bg} strokeWidth={1.5} opacity={0.3} />}
    </g>
  );
}

function DragSub({
  id,
  nx,
  ny,
  zoneNy,
  w,
  h,
  onMove,
  sel,
  onSel,
}: {
  id: SubstituteKey;
  nx: number;
  ny: number;
  zoneNy?: number;
  w: number;
  h: number;
  onMove: (id: SubstituteKey, pos: Pos) => void;
  sel: boolean;
  onSel: (id: SubstituteKey) => void;
}) {
  const subConfig = SUB_PC[id];
  const c = PC[subConfig.baseRole];
  const x = nx * w,
    y = ny * h,
    R = 16; // Légèrement plus petit que les joueurs sur le terrain
  const drag = useRef(false);

  const down = useCallback(
    (e: React.MouseEvent<SVGGElement> | React.TouchEvent<SVGGElement>) => {
      e.stopPropagation();
      e.preventDefault();
      drag.current = true;
      onSel(id);

      const svg = (e.currentTarget as SVGGElement).closest("svg") as SVGSVGElement | null;
      if (!svg) return;

      const gp = (ev: MouseEvent | TouchEvent) => {
        const r = svg.getBoundingClientRect();
        const touch = "touches" in ev ? ev.touches[0] : undefined;
        const cx = touch ? touch.clientX : (ev as MouseEvent).clientX;
        const cy = touch ? touch.clientY : (ev as MouseEvent).clientY;
        return {
          nx: Math.max(0.01, Math.min(0.99, (cx - r.left) / r.width)),
          ny: Math.max(0.01, Math.min(0.99, (cy - r.top) / r.height)),
        };
      };

      const mv = (ev: MouseEvent | TouchEvent) => {
        if (!drag.current) return;
        onMove(id, gp(ev));
      };

      const up = () => {
        drag.current = false;
        window.removeEventListener("mousemove", mv);
        window.removeEventListener("mouseup", up);
        window.removeEventListener("touchmove", mv);
        window.removeEventListener("touchend", up);
      };

      window.addEventListener("mousemove", mv);
      window.addEventListener("mouseup", up);
      window.addEventListener("touchmove", mv, { passive: false });
      window.addEventListener("touchend", up);
    },
    [id, onMove, onSel]
  );

  const isLibero = subConfig.baseRole === "Lib";
  const zRef = zoneNy ?? ny;
  const isAvant = zRef > MY + LH * 0.12 && zRef < MY + LH * 0.42;
  const isArriere = zRef >= MY + LH * 0.42 && zRef <= MY + LH;
  const zoneGlowColor = isAvant ? "#ff8800" : isArriere ? "#00d4b4" : null;
  
  return (
    <g style={{ cursor: "grab" }} onMouseDown={down} onTouchStart={down}>
      {zoneGlowColor && <circle cx={x} cy={y} r={R + 11} fill={zoneGlowColor} opacity={0.55} />}
      {sel && <circle cx={x} cy={y} r={R + 7} fill={c.bg} opacity={0.25} />}
      {isLibero && <circle cx={x} cy={y} r={R + 4} fill="none" stroke="#10b981" strokeWidth={2} opacity={0.7} />}
      <circle cx={x} cy={y} r={R} fill={c.bg} stroke={sel ? "#fff" : isLibero ? "#10b981" : "rgba(255,255,255,.4)"} strokeWidth={sel ? 2.5 : isLibero ? 2 : 1.5} opacity={0.85} />
      {(() => { const lbl = zoneRoleLabel(subConfig.baseRole, zRef); return (
        <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="central" fontSize={lbl.length > 3 ? 7 : 7.5} fontWeight={700} fill="white" fontFamily="monospace">
          {lbl}
        </text>
      ); })()}
    </g>
  );
}

function StatSub({
  id,
  nx,
  ny,
  w,
  h,
  glow,
  spotlight,
  dimmed,
  zoneNy,
}: {
  id: SubstituteKey;
  nx: number;
  ny: number;
  w: number;
  h: number;
  glow?: boolean;
  spotlight?: boolean;
  dimmed?: boolean;
  zoneNy?: number;
}) {
  const subConfig = SUB_PC[id];
  const c = PC[subConfig.baseRole];
  const x = nx * w,
    y = ny * h,
    R = 16;
  const opacity = dimmed ? 0.25 : 0.85;
  const isLibero = subConfig.baseRole === "Lib";
  const zRef = zoneNy ?? ny;
  const isAvant = zRef > MY + LH * 0.12 && zRef < MY + LH * 0.42;
  const isArriere = zRef >= MY + LH * 0.42 && zRef <= MY + LH;
  const zoneGlowColor = isAvant ? "#ff8800" : isArriere ? "#00d4b4" : null;
  
  return (
    <g opacity={opacity}>
      {spotlight && <circle cx={x} cy={y} r={R + 12} fill={c.bg} opacity={0.18} />}
      {spotlight && <circle cx={x} cy={y} r={R + 7} fill="none" stroke={c.bg} strokeWidth={2} strokeDasharray="3,3" opacity={0.7} />}
      {zoneGlowColor && <circle cx={x} cy={y} r={R + 11} fill={zoneGlowColor} opacity={0.55} />}
      {glow && <circle cx={x} cy={y} r={R + 8} fill={c.bg} opacity={0.22} stroke="#fff" strokeWidth={1} strokeDasharray="3,2" />}
      {isLibero && <circle cx={x} cy={y} r={R + 4} fill="none" stroke="#10b981" strokeWidth={2} opacity={0.7} />}
      <circle
        cx={x}
        cy={y}
        r={R}
        fill={c.bg}
        stroke={spotlight ? "#fff" : glow ? "#fff" : isLibero ? "#10b981" : "rgba(255,255,255,.35)"}
        strokeWidth={spotlight ? 3 : glow ? 2.5 : isLibero ? 2 : 1.5}
      />
      <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="central" fontSize={7.5} fontWeight={700} fill="white" fontFamily="monospace">
        {zoneRoleLabel(subConfig.baseRole, zRef)}
      </text>
      {spotlight && <circle cx={x} cy={y} r={R + 14} fill="none" stroke={c.bg} strokeWidth={1.5} opacity={0.3} />}
    </g>
  );
}

function Arrow({
  x1,
  y1,
  x2,
  y2,
  color,
  label,
  prog,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  label?: string;
  prog: number;
}) {
  const dx = x2 - x1,
    dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  const ux = dx / len,
    uy = dy / len,
    px = -uy,
    py = ux;

  const ex = x1 + dx * prog,
    ey = y1 + dy * prog;
  const hw = 5;
  const bx = ex - ux * 10,
    by = ey - uy * 10;

  return (
    <g>
      <line x1={x1} y1={y1} x2={ex - ux * 9} y2={ey - uy * 9} stroke={color} strokeWidth={2.5} strokeDasharray="6,4" opacity={0.92} />
      {prog >= 0.98 && <polygon points={`${ex},${ey} ${bx + px * hw},${by + py * hw} ${bx - px * hw},${by - py * hw}`} fill={color} opacity={0.92} />}
      {prog >= 0.98 && label && (
        <text
          x={(x1 + ex) / 2 + px * 14}
          y={(y1 + ey) / 2 + py * 14}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={8}
          fill={color}
          fontFamily="monospace"
          fontWeight={600}
        >
          {label}
        </text>
      )}
    </g>
  );
}

function Ball({
  nx,
  ny,
  w,
  h,
  traj = 0,
  drag = false,
  sel = false,
  onDown,
}: {
  nx: number;
  ny: number;
  w: number;
  h: number;
  traj?: number;
  drag?: boolean;
  sel?: boolean;
  onDown?: (e: React.MouseEvent<SVGGElement> | React.TouchEvent<SVGGElement>) => void;
}) {
  const x = nx * w;
  const y = ny * h;
  const arc = Math.sin(traj * Math.PI);
  const R = 13 + arc * 11;
  const shadowOp = Math.max(0.12, 0.4 - arc * 0.25);

  return (
    <g style={{ cursor: drag ? "grab" : "default" }} onMouseDown={onDown} onTouchStart={onDown}>
      <ellipse cx={x} cy={y + 5} rx={13 * (1 - arc * 0.3)} ry={5 * (1 - arc * 0.3)} fill="rgba(0,0,0,.3)" opacity={shadowOp} />
      <circle cx={x} cy={y} r={R} fill="#f5f0e8" stroke="rgba(0,0,0,.12)" strokeWidth={1} />
      <path d={`M${x - R},${y} Q${x},${y - R * 0.55} ${x + R},${y}`} fill="none" stroke="rgba(100,80,40,.38)" strokeWidth={1} />
      <path d={`M${x},${y - R} Q${x + R * 0.55},${y} ${x},${y + R}`} fill="none" stroke="rgba(100,80,40,.38)" strokeWidth={1} />
      <path
        d={`M${x - R * 0.65},${y - R * 0.65} Q${x + R * 0.25},${y + R * 0.25} ${x + R * 0.65},${y + R * 0.65}`}
        fill="none"
        stroke="rgba(100,80,40,.28)"
        strokeWidth={1}
      />
      <circle cx={x - R * 0.3} cy={y - R * 0.32} r={R * 0.22} fill="rgba(255,255,255,.6)" />
      {sel && <circle cx={x} cy={y} r={R + 6} fill="none" stroke="#fbbf24" strokeWidth={2} strokeDasharray="3,2" />}
    </g>
  );
}

function DragBall({
  ball,
  w,
  h,
  onMove,
  sel,
  onSel,
}: {
  ball: BallPos;
  w: number;
  h: number;
  onMove: (ball: BallPos) => void;
  sel: boolean;
  onSel: () => void;
}) {
  const drag = useRef(false);

  const down = useCallback(
    (e: React.MouseEvent<SVGGElement> | React.TouchEvent<SVGGElement>) => {
      e.stopPropagation();
      e.preventDefault();
      drag.current = true;
      onSel();

      const svg = (e.currentTarget as SVGGElement).closest("svg") as SVGSVGElement | null;
      if (!svg) return;

      const gp = (ev: MouseEvent | TouchEvent) => {
        const r = svg.getBoundingClientRect();
        const touch = "touches" in ev ? ev.touches[0] : undefined;
        const cx = touch ? touch.clientX : (ev as MouseEvent).clientX;
        const cy = touch ? touch.clientY : (ev as MouseEvent).clientY;
        return {
          nx: Math.max(0.01, Math.min(0.99, (cx - r.left) / r.width)),
          ny: Math.max(0.01, Math.min(0.99, (cy - r.top) / r.height)),
        };
      };

      const mv = (ev: MouseEvent | TouchEvent) => {
        if (!drag.current) return;
        onMove({ ...ball, ...gp(ev) });
      };

      const up = () => {
        drag.current = false;
        window.removeEventListener("mousemove", mv);
        window.removeEventListener("mouseup", up);
        window.removeEventListener("touchmove", mv);
        window.removeEventListener("touchend", up);
      };

      window.addEventListener("mousemove", mv);
      window.addEventListener("mouseup", up);
      window.addEventListener("touchmove", mv, { passive: false });
      window.addEventListener("touchend", up);
    },
    [ball, onMove, onSel]
  );

  return <Ball nx={ball.nx} ny={ball.ny} w={w} h={h} traj={0} drag sel={sel} onDown={down} />;
}

function SmBtn({
  children,
  onClick,
  bg = "#2563eb",
  outline = false,
  disabled = false,
  textColor,
}: {
  children: React.ReactNode;
  onClick: () => void;
  bg?: string;
  outline?: boolean;
  disabled?: boolean;
  textColor?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "7px 16px",
        borderRadius: 7,
        border: outline ? "1.5px solid #d1d5db" : "none",
        background: outline ? "#fff" : disabled ? "#e5e7eb" : bg,
        color: disabled ? "#9ca3af" : textColor || (outline ? "#374151" : "#fff"),
        fontSize: 12,
        fontWeight: 700,
        cursor: disabled ? "default" : "pointer",
        fontFamily: "inherit",
        boxShadow: outline ? "none" : "0 1px 3px rgba(0,0,0,.1)",
      }}
    >
      {children}
    </button>
  );
}

function TypePill({ type }: { type: SchemaType }) {
  return type === "service" ? (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: 20,
        fontSize: 10,
        fontWeight: 700,
        background: "#fef9c3",
        color: "#713f12",
        border: "1px solid #fcd34d",
      }}
    >
      ⚡ Service
    </span>
  ) : (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: 20,
        fontSize: 10,
        fontWeight: 700,
        background: "#dbeafe",
        color: "#1e3a8a",
        border: "1px solid #93c5fd",
      }}
    >
      🛡 Réception
    </span>
  );
}

function SideCard({
  schema,
  tacticLabel,
  active,
  onClick,
  onEdit,
  onDelete,
  onDuplicate,
  onPlay,
  isAdmin,
}: {
  schema: Schema;
  tacticLabel?: string;
  active: boolean;
  onClick: (schema: Schema) => void;
  onEdit: (schema: Schema) => void;
  onDelete: (id: string) => void;
  onDuplicate: (schema: Schema) => void;
  onPlay: (schema: Schema) => void;
  isAdmin: boolean;
}) {
  const tactic = String(tacticLabel ?? schema.tactic ?? "").trim();
  return (
    <div
      onClick={() => onClick(schema)}
      style={{
        background: active ? "#eff6ff" : "#fff",
        borderRadius: 8,
        border: `1.5px solid ${active ? "#3b82f6" : "#e5e7eb"}`,
        padding: "8px 10px",
        cursor: "pointer",
        boxShadow: active ? "0 0 0 2px rgba(59,130,246,.15)" : "0 1px 2px rgba(0,0,0,.04)",
        transition: "all .12s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 18, flexShrink: 0, lineHeight: 1 }}>{schema.type === "service" ? "⚡" : "🛡"}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: active ? "#1d4ed8" : "#111827",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.3,
            }}
          >
            {schema.name}
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 3, alignItems: "center" }}>
            <TypePill type={schema.type} />
            {tactic ? (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #e5e7eb",
                  maxWidth: 140,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={tactic}
              >
                📁 {tactic}
              </span>
            ) : null}
          </div>
        </div>

        <div style={{ display: "flex", gap: 3, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onPlay(schema)}
            title="Animer"
            style={{
              width: 22,
              height: 22,
              borderRadius: 5,
              border: "none",
              cursor: "pointer",
              background: "#16a34a",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              boxShadow: "0 1px 4px #16a34a44",
            }}
          >
            ▶
          </button>
          {isAdmin && (
            <button
              onClick={() => onEdit(schema)}
              title="Modifier"
              style={{
                width: 22,
                height: 22,
                borderRadius: 5,
                border: "1px solid #e5e7eb",
                background: "#f9fafb",
                color: "#6b7280",
                fontSize: 10,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            >
              ✏️
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => onDuplicate(schema)}
              title="Dupliquer"
              style={{
                width: 22,
                height: 22,
                borderRadius: 5,
                border: "1px solid #bfdbfe",
                background: "#eff6ff",
                color: "#2563eb",
                fontSize: 10,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            >
              ⎘
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => onDelete(schema.id)}
              title="Supprimer"
              style={{
                width: 22,
                height: 22,
                borderRadius: 5,
                border: "1px solid #fecaca",
                background: "#fff",
                color: "#dc2626",
                fontSize: 10,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AniCourt({
  schema,
  w,
  h,
  myRole,
  autoTrigger,
  onAutoTriggered,
  onPlayStart,
}: {
  schema: Schema;
  w: number;
  h: number;
  myRole: MyRoleKey | null;
  autoTrigger: boolean;
  onAutoTriggered?: () => void;
  onPlayStart?: (info: { trigger: "manual" | "auto"; startIdx: number }) => void;
}) {
  const steps = useMemo<Step[]>(() => {
    if (schema.steps && schema.steps.length >= 2) return schema.steps;

    return [
      { label: "Réglementaire", positions: schema.base },
      { label: "Jeu", positions: schema.perm },
    ];
  }, [schema.base, schema.perm, schema.steps]);

  const [stepIdx, setStepIdx] = useState(0);
  const [, setTick] = useState(0);
  const st = useRef<{ phase: "idle" | "focus" | "run" | "done"; prog: number; pos: Positions | null; subs: Substitutes | null }>({
    phase: "idle",
    prog: 0,
    pos: null,
    subs: null,
  });
  const raf = useRef<number | null>(null);
  const t0 = useRef<number | null>(null);
  // Un peu plus lent pour une meilleure lecture
  const DURATION = 2200;
  const INTRO_DURATION = 3200;
  const ROLE_CHANGE_DURATION = 3600;
  const FOCUS_DURATION = 3200;

  const focusRef = useRef<null | { playerId: PlayerKey; prog: number; fromRole: PlayerKey; toRole: PlayerKey }>(null);

  const [roleModal, setRoleModal] = useState<
    | null
    | {
        fromIdx: number;
        toIdx: number;
        changes: Array<{
          playerId: PlayerKey;
          fromRole: PlayerKey;
          toRole: PlayerKey;
          zone: string;
          fromLabel: string;
          toLabel: string;
        }>;
      }
  >(null);
  const [placementPause, setPlacementPause] = useState<null | { atIdx: number; nextIdx: number }>(null);
  const pendingRoleSeg = useRef<number | null>(null);

  const ids = Object.keys(steps[0]?.positions ?? {}) as PlayerKey[];
  const subIds = Object.keys(steps[0]?.substitutes ?? {}) as SubstituteKey[];

  useEffect(() => {
    if (raf.current) cancelAnimationFrame(raf.current);
    st.current = { phase: "idle", prog: 0, pos: null, subs: null };
    t0.current = null;
    setStepIdx(0);
    setRoleModal(null);
    setPlacementPause(null);
    pendingRoleSeg.current = null;
    focusRef.current = null;
    setTick((n) => n + 1);
  }, [schema.id]);

  const redraw = useCallback(() => setTick((n) => n + 1), []);

  const computeRoleChanges = useCallback(
    (fromIdx: number, toIdx: number) => {
      const fromRoles = ((steps[fromIdx] as any)?.roles as RoleMap | undefined) || {};
      const toRoles = ((steps[toIdx] as any)?.roles as RoleMap | undefined) || {};
      const toPositions = steps[toIdx]?.positions || {};

      return ids
        .map((playerId) => {
          const fromRole: PlayerKey = fromRoles[playerId] ?? playerId;
          const toRole: PlayerKey = toRoles[playerId] ?? playerId;
          if (fromRole === toRole) return null;
          const p = toPositions[playerId];
          const zone = p ? getZone(p) : "?";
          return {
            playerId,
            fromRole,
            toRole,
            zone,
            fromLabel: PC[fromRole].label,
            toLabel: PC[toRole].label,
          };
        })
        .filter(Boolean) as Array<{
        playerId: PlayerKey;
        fromRole: PlayerKey;
        toRole: PlayerKey;
        zone: string;
        fromLabel: string;
        toLabel: string;
      }>;
    },
    [ids, steps]
  );

  const runSegment = useCallback(
    (i: number, opts?: { skipRoleModal?: boolean }) => {
      if (i >= steps.length - 1) {
        st.current.pos = steps[steps.length - 1].positions;
        st.current.subs = steps[steps.length - 1].substitutes || null;
        st.current.prog = 1;
        st.current.phase = "done";
        setPlacementPause(null);
        setStepIdx(steps.length - 1);
        redraw();
        return;
      }

      const from = steps[i].positions;
      const to = steps[i + 1].positions;
      const fromSubs = steps[i].substitutes || {};
      const toSubs = steps[i + 1].substitutes || {};

      const anyMove = Object.keys(from).some((id) => {
        const fid = id as PlayerKey;
        const fp = from[fid];
        const tp = to[fid];
        if (!fp || !tp) return false;
        return gap(fp, tp) > 0.015;
      });

      const anySubMove = Object.keys(fromSubs).some((id) => {
        const sid = id as SubstituteKey;
        const fp = fromSubs[sid];
        const tp = toSubs[sid];
        if (!fp || !tp) return false;
        return gap(fp, tp) > 0.015;
      });

      const changes = computeRoleChanges(i, i + 1);

      if (changes.length > 0 && !opts?.skipRoleModal) {
        pendingRoleSeg.current = i;
        setRoleModal({ fromIdx: i, toIdx: i + 1, changes });
        if (raf.current) cancelAnimationFrame(raf.current);
        t0.current = null;
        st.current.phase = "run";
        st.current.prog = 0;
        st.current.pos = { ...from };
        st.current.subs = steps[i].substitutes ? { ...fromSubs } : null;
        setStepIdx(i);
        redraw();
        return;
      }

      const isIntroSegment = i === 0;
      const baseDur = anyMove || anySubMove ? (isIntroSegment ? INTRO_DURATION : DURATION) : 300;
      const dur = changes.length > 0 ? Math.max(baseDur, ROLE_CHANGE_DURATION) : baseDur;

      if (raf.current) cancelAnimationFrame(raf.current);
      t0.current = null;
      st.current.phase = "run";
      st.current.prog = 0;
      st.current.pos = { ...from };
      st.current.subs = steps[i].substitutes ? { ...fromSubs } : null;
      setStepIdx(i);
      redraw();

      const frame = (now: number) => {
        if (!t0.current) t0.current = now;
        const elapsed = now - t0.current;
        const localT = Math.max(0, Math.min(1, elapsed / dur));
        const e = isIntroSegment ? eioIntro(localT) : eioSmooth(localT);

        const ip: Positions = {};
        Object.keys(from).forEach((id) => {
          const pid = id as PlayerKey;
          const f = from[pid];
          const t = to[pid];
          if (!f || !t) return;
          ip[pid] = {
            nx: f.nx + (t.nx - f.nx) * e,
            ny: f.ny + (t.ny - f.ny) * e,
          };
        });

        const iSubs: Substitutes = {};
        Object.keys(fromSubs).forEach((id) => {
          const sid = id as SubstituteKey;
          const f = fromSubs[sid];
          const t = toSubs[sid];
          if (!f || !t) return;
          iSubs[sid] = {
            nx: f.nx + (t.nx - f.nx) * e,
            ny: f.ny + (t.ny - f.ny) * e,
          };
        });

        st.current.prog = localT;
        st.current.pos = ip;
        st.current.subs = iSubs;
        redraw();

        if (localT < 1) {
          raf.current = requestAnimationFrame(frame);
        } else {
          st.current.pos = to;
          st.current.subs = steps[i + 1].substitutes || null;
          st.current.prog = 1;
          st.current.phase = "idle";
          setStepIdx(i + 1);
          redraw();
          if (schema.type === "reception" && i === 0 && steps.length > 2) {
            setPlacementPause({ atIdx: i + 1, nextIdx: i + 1 });
            return;
          }
          runSegment(i + 1);
        }
      };

      raf.current = requestAnimationFrame(frame);
    },
    [computeRoleChanges, redraw, schema.type, steps]
  );

  const animateChain = useCallback(
    (startIdx: number) => {
      setRoleModal(null);
      setPlacementPause(null);
      pendingRoleSeg.current = null;
      focusRef.current = null;
      if (raf.current) cancelAnimationFrame(raf.current);
      t0.current = null;

      st.current = {
        phase: "run",
        prog: 0,
        pos: { ...steps[startIdx].positions },
        subs: steps[startIdx].substitutes ? { ...(steps[startIdx].substitutes || {}) } : null,
      };
      setStepIdx(startIdx);
      redraw();

      runSegment(startIdx);
    },
    [redraw, runSegment, steps]
  );

  useEffect(() => {
    if (!autoTrigger) return;
    const timer = window.setTimeout(() => {
      if (raf.current) cancelAnimationFrame(raf.current);
      st.current = { phase: "idle", prog: 0, pos: null, subs: null };
      t0.current = null;
      setStepIdx(0);
      onPlayStart?.({ trigger: "auto", startIdx: 0 });
      animateChain(0);
      onAutoTriggered?.();
    }, 300);

    return () => window.clearTimeout(timer);
  }, [autoTrigger, animateChain, onAutoTriggered, onPlayStart]);

  const play = () => {
    if (raf.current) cancelAnimationFrame(raf.current);
    st.current = { phase: "idle", prog: 0, pos: null, subs: null };
    t0.current = null;
    setRoleModal(null);
    setPlacementPause(null);
    pendingRoleSeg.current = null;
    focusRef.current = null;
    const startFrom = stepIdx < steps.length - 1 ? stepIdx : 0;
    if (startFrom === 0) setStepIdx(0);
    onPlayStart?.({ trigger: "manual", startIdx: startFrom });
    animateChain(startFrom);
  };

  const reset = () => {
    if (raf.current) cancelAnimationFrame(raf.current);
    st.current = { phase: "idle", prog: 0, pos: null, subs: null };
    setStepIdx(0);
    setRoleModal(null);
    setPlacementPause(null);
    pendingRoleSeg.current = null;
    focusRef.current = null;
    redraw();
  };

  const confirmPlacementPause = () => {
    const pause = placementPause;
    setPlacementPause(null);
    if (!pause) return;
    runSegment(pause.nextIdx);
  };

  const goToSpecificStep = (targetIdx: number) => {
    if (raf.current) cancelAnimationFrame(raf.current);
    t0.current = null;
    st.current = {
      phase: "idle",
      prog: 0,
      pos: { ...steps[targetIdx].positions },
      subs: steps[targetIdx].substitutes ? { ...(steps[targetIdx].substitutes || {}) } : null,
    };
    setRoleModal(null);
    setPlacementPause(null);
    pendingRoleSeg.current = null;
    focusRef.current = null;
    setStepIdx(targetIdx);
    redraw();
  };

  const confirmRoleModal = () => {
    const i = pendingRoleSeg.current;
    const modal = roleModal;
    setRoleModal(null);
    pendingRoleSeg.current = null;
    if (i == null) return;

    const changes = modal?.changes ?? computeRoleChanges(i, i + 1);
    const focusChange = changes[0];
    const focusId = focusChange?.playerId;
    if (!focusId || !focusChange) {
      runSegment(i, { skipRoleModal: true });
      return;
    }

    // Phase "focus": zoom + fade lent sur la pastille, avant de continuer l'anim.
    focusRef.current = { playerId: focusId, prog: 0, fromRole: focusChange.fromRole, toRole: focusChange.toRole };
    if (raf.current) cancelAnimationFrame(raf.current);
    t0.current = null;
    st.current.phase = "focus";
    st.current.prog = 0;
    st.current.pos = { ...steps[i].positions };
    st.current.subs = steps[i].substitutes ? { ...(steps[i].substitutes || {}) } : null;
    setStepIdx(i);
    redraw();

    const frame = (now: number) => {
      if (!t0.current) t0.current = now;
      const elapsed = now - t0.current;
      const localT = Math.max(0, Math.min(1, elapsed / FOCUS_DURATION));
      if (focusRef.current) focusRef.current.prog = localT;
      redraw();

      if (localT < 1) {
        raf.current = requestAnimationFrame(frame);
      } else {
        focusRef.current = null;
        t0.current = null;
        runSegment(i, { skipRoleModal: true });
      }
    };

    raf.current = requestAnimationFrame(frame);
  };

  const { phase, prog, pos, subs } = st.current;

  const shownIdx = phase === "run" || phase === "focus" ? Math.min(stepIdx + 1, steps.length - 1) : Math.min(stepIdx, steps.length - 1);
  const shownRoles = (steps[shownIdx] as any)?.roles as RoleMap | undefined;

  const orderedPlayers = schema.playerCount === 6 ? P6 : P4;
  const orderIndex = new Map<PlayerKey, number>(orderedPlayers.map((k, i) => [k, i] as const));
  const sortByOrder = (a: PlayerKey, b: PlayerKey) => (orderIndex.get(a) ?? 99) - (orderIndex.get(b) ?? 99) || a.localeCompare(b);
  const effectiveRoleOf = (playerId: PlayerKey): PlayerKey => shownRoles?.[playerId] || playerId;
  const r4Players = ids.filter((pid) => effectiveRoleOf(pid) === "R4").slice().sort(sortByOrder);
  const r4IdxOf = (pid: PlayerKey) => r4Players.indexOf(pid);
  const roleKeyFor = (pid: PlayerKey, role: PlayerKey): MyRoleKey => {
    if (role === "R4" && r4Players.length >= 2) {
      const idx = r4IdxOf(pid);
      if (idx === 0) return "R4_1";
      if (idx === 1) return "R4_2";
      return "R4";
    }
    return role;
  };
  const roleTextFor = (pid: PlayerKey, role: PlayerKey): string => {
    if (role === "R4" && r4Players.length >= 2) {
      const idx = r4IdxOf(pid);
      return idx >= 0 ? `R4 ${idx + 1}` : "R4";
    }
    return shortRoleCode(role);
  };

  const resolvedMyRole: MyRoleKey | null = useMemo(() => {
    if (!myRole) return null;
    // Si on clique une remplaçante, on met en avant le poste de base sur le terrain.
    const base = baseRoleOfMyRole(myRole);
    if (base === "R4" && r4Players.length >= 2) return "R4_1";
    return base;
  }, [myRole, r4Players.length]);

  const displayPos = (pos || steps[Math.min(stepIdx, steps.length - 1)].positions) as Positions;
  const displaySubs = (subs || steps[Math.min(stepIdx, steps.length - 1)].substitutes || {}) as Substitutes;
  const fromPos = steps[Math.min(stepIdx, steps.length - 1)].positions;
  const toPos = steps[Math.min(stepIdx + 1, steps.length - 1)].positions;
  const fromSubs = steps[Math.min(stepIdx, steps.length - 1)].substitutes || {};
  const toSubs = steps[Math.min(stepIdx + 1, steps.length - 1)].substitutes || {};
  // Positions réglementaires (step 0) — référence fixe pour les zones de glow
  const regPos = steps[0].positions;
  const regSubs = steps[0].substitutes || {};

  const moved = ids.filter((id) => {
    const fp = fromPos[id];
    const tp = toPos[id];
    if (!fp || !tp) return false;
    return gap(fp, tp) > 0.015;
  });
  
  const movedSubs = subIds.filter((id) => {
    const fp = fromSubs[id];
    const tp = toSubs[id];
    if (!fp || !tp) return false;
    return gap(fp, tp) > 0.015;
  });

  const isLast = stepIdx >= steps.length - 1;
  const isDone = phase === "done";

  return (
    <div>
      <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
        {phase !== "run" && !isLast && !isDone && (
          <SmBtn onClick={play} bg="#2563eb">
            {stepIdx === 0 ? "▶ Animer tout" : `▶ Suite (étape ${stepIdx + 1}→${steps.length})`}
          </SmBtn>
        )}
        {phase === "run" && <SmBtn onClick={reset} bg="#64748b">⏹ Stop</SmBtn>}
        {(isLast || isDone) && phase !== "run" && <SmBtn onClick={reset} bg="#64748b">↺ Début</SmBtn>}
        <span style={{ fontSize: 11, color: "#6b7280", fontStyle: "italic" }}>
          {steps[Math.min(stepIdx + (isDone ? 1 : 0), steps.length - 1)].label}
        </span>
      </div>

      <div style={{ position: "relative", width: w, height: h, margin: "0 auto" }}>
        <Court w={w} h={h}>
        {(phase === "run" || isDone) &&
          moved.map((id) => {
            const fp = fromPos[id];
            const tp = toPos[id];
            if (!fp || !tp) return null;
            return (
              <Arrow
                key={id}
                x1={fp.nx * w}
                y1={fp.ny * h}
                x2={tp.nx * w}
                y2={tp.ny * h}
                color={id === "Pass" ? "#ef4444" : "#8b5cf6"}
                label={id}
                prog={isDone ? 1 : prog}
              />
            );
          })}
        
        {(phase === "run" || isDone) &&
          movedSubs.map((id) => {
            const fp = fromSubs[id];
            const tp = toSubs[id];
            if (!fp || !tp) return null;
            const baseRole = SUB_PC[id].baseRole;
            return (
              <Arrow
                key={id}
                x1={fp.nx * w}
                y1={fp.ny * h}
                x2={tp.nx * w}
                y2={tp.ny * h}
                color={PC[baseRole].bg}
                label={shortRoleCode(baseRole)}
                prog={isDone ? 1 : prog}
              />
            );
          })}

        {ids.map((id) => {
          const p = displayPos[id];
          if (!p) return null;
          const displayRole = effectiveRoleOf(id);
          const roleKey = roleKeyFor(id, displayRole);
          const isSelected = !!resolvedMyRole && roleKey === resolvedMyRole;
          const focus = focusRef.current;
          const isFocused = !!focus && focus.playerId === id;
          return (
            <StatP
              key={id}
              id={id}
              role={displayRole}
              text={roleTextFor(id, displayRole)}
              nx={p.nx}
              ny={p.ny}
              zoneNy={regPos[id]?.ny ?? p.ny}
              w={w}
              h={h}
              glow={isDone && moved.includes(id)}
              spotlight={isSelected}
              dimmed={!!resolvedMyRole && !isSelected}
              focusProg={isFocused ? focus!.prog : undefined}
              focusDimOthers={!!focus && !isFocused}
              transitionFromRole={isFocused ? focus!.fromRole : undefined}
              transitionToRole={isFocused ? focus!.toRole : undefined}
              transitionT={isFocused ? focus!.prog : undefined}
            />
          );
        })}
        
        {subIds.map((id) => {
          const p = displaySubs[id];
          if (!p) return null;
          return <StatSub key={id} id={id} nx={p.nx} ny={p.ny} zoneNy={regSubs[id]?.ny ?? p.ny} w={w} h={h} glow={isDone && movedSubs.includes(id)} dimmed={!!resolvedMyRole} />;
        })}

        {(() => {
          const ballFrom = steps[Math.min(stepIdx, steps.length - 1)]?.ball;
          const ballTo = steps[Math.min(stepIdx + 1, steps.length - 1)]?.ball;
          const hasBall = !!ballFrom || !!ballTo;
          if (!hasBall) return null;

          const bf = ballFrom || ballTo;
          const bt = ballTo || ballFrom;
          if (!bf || !bt) return null;

          const p = phase === "idle" ? 0 : isDone ? 1 : prog;
          const bx = bf.nx + (bt.nx - bf.nx) * p;
          const by = bf.ny + (bt.ny - bf.ny) * p;

          const ballMoves = Math.abs(bf.nx - bt.nx) > 0.01 || Math.abs(bf.ny - bt.ny) > 0.01;
          const traj = ballMoves ? p : 0;
          return <Ball key="ball" nx={bx} ny={by} w={w} h={h} traj={traj} />;
        })()}
        </Court>

        {roleModal && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,.42)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              zIndex: 5,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "min(520px, 100%)",
                background: "#fff",
                borderRadius: 12,
                border: "2px solid #111827",
                boxShadow: "0 14px 40px rgba(0,0,0,.35)",
                padding: 14,
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontWeight: 900, color: "#111827", fontSize: 13 }}>🔄 Changement de poste</div>
                <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700 }}>
                  Étape {roleModal.fromIdx + 1} → {roleModal.toIdx + 1}
                </div>
              </div>

              <div style={{ marginTop: 10, display: "flex", gap: 4, flexWrap: "wrap" }}>
                {steps.map((s, i) => {
                  const active = i === stepIdx;
                  return (
                    <button
                      key={i}
                      onClick={() => goToSpecificStep(i)}
                      style={{
                        padding: "4px 10px",
                        borderRadius: 999,
                        border: `1.5px solid ${active ? "#2563eb" : "#dbeafe"}`,
                        background: active ? "#2563eb" : "#eff6ff",
                        color: active ? "#fff" : "#1d4ed8",
                        fontSize: 10,
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      {i + 1}. {s.label}
                    </button>
                  );
                })}
              </div>

              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                {roleModal.changes.map((c, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 10px",
                      borderRadius: 10,
                      background: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      fontSize: 12,
                      color: "#111827",
                      fontWeight: 700,
                    }}
                  >
                    <span style={{ width: 10, height: 10, borderRadius: 999, background: PC[c.fromRole].bg, flexShrink: 0 }} />
                    <span style={{ color: PC[c.fromRole].bg, fontFamily: "monospace" }}>{shortRoleCode(c.fromRole)}</span>
                    <span style={{ color: "#6b7280", fontWeight: 800 }}>→</span>
                    <span style={{ width: 10, height: 10, borderRadius: 999, background: PC[c.toRole].bg, flexShrink: 0 }} />
                    <span style={{ color: PC[c.toRole].bg, fontFamily: "monospace" }}>{shortRoleCode(c.toRole)}</span>
                    <span style={{ marginLeft: "auto", color: "#6b7280", fontWeight: 800 }}>({c.zone})</span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 10, fontSize: 11, color: "#374151", lineHeight: 1.4, fontWeight: 600 }}>
                Valide pour continuer l’animation et voir le mouvement avec le nouveau poste.
              </div>

              <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={confirmRoleModal}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 10,
                    border: "2px solid #2563eb",
                    background: "#2563eb",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  J’ai compris
                </button>
              </div>
            </div>
          </div>
        )}

        {placementPause && !roleModal && (
          <div
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 4,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
              <SmBtn onClick={confirmPlacementPause} bg="#e5e7eb" textColor="#374151">
              💡 J’ai compris le placement, voir la suite
            </SmBtn>
          </div>
        )}
      </div>

      {/* Affichage des changements de rôle dans l'animation */}
      {(() => {
        if (shownIdx === 0) return null;
        const roleMap = (steps[shownIdx] as any)?.roles as RoleMap | undefined;
        if (!roleMap) return null;

        const ids = Object.keys(steps[shownIdx].positions ?? {}) as PlayerKey[];
        const changes = ids
          .map((pid) => {
            const fromRole = pid;
            const toRole = roleMap?.[pid] || pid;
            if (toRole === fromRole) return null;
            const p = steps[shownIdx].positions[pid];
            const zone = p ? getZone(p) : "?";
            return {
              pid,
              fromRole,
              toRole,
              zone,
              fromLabel: PC[fromRole].label,
              toLabel: PC[toRole].label,
            };
          })
          .filter(Boolean) as Array<{ pid: PlayerKey; fromRole: PlayerKey; toRole: PlayerKey; zone: string; fromLabel: string; toLabel: string }>;

        if (changes.length === 0) return null;
        return (
          <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
            <div style={{ 
              background: "#fef3c7", 
              border: "1.5px solid #fbbf24", 
              borderRadius: 8, 
              padding: "8px 12px",
              fontSize: 11,
              color: "#78350f",
              fontWeight: 600,
              lineHeight: 1.5,
              maxWidth: 460,
            }}>
              <div style={{ marginBottom: 4, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em" }}>
                🔄 Changements de poste :
              </div>
              {changes.map((c, i) => (
                <div key={i}>
                  La <strong>{c.fromLabel}</strong> joue en <strong>{c.toLabel}</strong> dans la <strong>{c.zone}</strong>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {(isDone || isLast) && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "center", marginTop: 8 }}>
          {moved.length > 0 && (
            <>
              {moved.map((id) => (
                <span key={id} style={{ padding: "2px 8px", borderRadius: 12, fontSize: 10, fontWeight: 700, background: PC[id].bg + "33", color: PC[id].bg }}>
                  {id}
                </span>
              ))}
              <span style={{ fontSize: 10, color: "#9ca3af", alignSelf: "center" }}>se déplacent</span>
            </>
          )}
          {movedSubs.length > 0 && (
            <>
              {movedSubs.map((id) => {
                const baseRole = SUB_PC[id].baseRole;
                return (
                  <span key={id} style={{ padding: "2px 8px", borderRadius: 12, fontSize: 10, fontWeight: 700, background: PC[baseRole].bg + "33", color: PC[baseRole].bg }}>
                    {baseRole} (banc)
                  </span>
                );
              })}
              <span style={{ fontSize: 10, color: "#9ca3af", alignSelf: "center" }}>remplacements</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function roleInstructions(id: PlayerKey, schema: Schema) {
  const base = schema.base[id];
  const perm = schema.perm[id];
  if (!base) return null;

  const moves = perm ? gap(base, perm) > 0.015 : false;
  const type = schema.type;

  const zoneOf = (pos: Pos) => {
    const col = pos.nx < 0.37 ? "gauche" : pos.nx > 0.63 ? "droite" : "centre";
    const row = pos.ny < 0.45 ? "avant" : "arrière";
    const zNum =
      pos.ny < 0.45
        ? pos.nx < 0.37
          ? "Z4"
          : pos.nx > 0.63
            ? "Z2"
            : "Z3"
        : pos.nx < 0.37
          ? "Z5"
          : pos.nx > 0.63
            ? "Z1"
            : "Z6";
    return { col, row, zNum };
  };

  const bz = zoneOf(base);
  const pz = perm ? zoneOf(perm) : bz;

  if (id === "Pass") {
    if (type === "service") {
      if (bz.zNum === "Z1") return { action: "🎯 Tu sers !", detail: `Tu pars de Z1 (fond droit) et tu montes en ${pz.zNum} pour distribuer.`, color: "#dc2626" };
      return { action: `📍 Tu es en ${bz.zNum}`, detail: `Après le service adverse, déplace-toi en ${pz.zNum} pour être en position de passe.`, color: "#dc2626" };
    }
    return {
      action: `📍 Tu es en ${bz.zNum}`,
      detail: moves ? `Remonte en ${pz.zNum} dès que le service part pour être prête à distribuer.` : `Tu es déjà en position — reste en ${pz.zNum} et sois prête.`,
      color: "#dc2626",
    };
  }

  if (id === "Lib") {
    if (type === "service" && bz.zNum === "Z1") return { action: "🎯 Tu sers !", detail: "Tu prends le service depuis Z1. Attention à rentrer vite en position arrière.", color: "#10b981" };
    return {
      action: `🛡 Réceptionneuse`,
      detail: `Tu es en ${bz.zNum} (${bz.row} ${bz.col}). ${moves ? `Déplace-toi en ${pz.zNum} pour couvrir la zone arrière.` : "Reste en position et gère la réception arrière."}`,
      color: "#10b981",
    };
  }

  if (id === "Poi") {
    if (type === "service" && bz.zNum === "Z1") return { action: "🎯 Tu sers !", detail: "Service depuis Z1. Ensuite rentre en position avant droite.", color: "#8b5cf6" };
    return {
      action: `⚡ Pointue`,
      detail: `Tu es en ${bz.zNum}. ${moves ? `Déplace-toi en ${pz.zNum} — tu seras l'attaquante principale.` : "Reste en position et prépare ton attaque."}`,
      color: "#8b5cf6",
    };
  }

  if (id === "R4") {
    return {
      action: `🏃 R4`,
      detail: `Tu pars de ${bz.zNum}. ${moves ? `Va te placer en ${pz.zNum} pour la réception/attaque.` : `Reste en ${bz.zNum} — maintiens ta position.`}`,
      color: "#f59e0b",
    };
  }

  const label = "Centrale";
  return {
    action: `🔒 ${label}`,
    detail: `Tu es en ${bz.zNum}. ${moves ? `Déplace-toi en ${pz.zNum} — monte au filet pour bloquer.` : `Reste en ${bz.zNum} et prépare-toi au filet.`}`,
    color: id === "C1" ? "#3b82f6" : "#ec4899",
  };
}

function SchemaModal({
  schema,
  myRole: initialRole,
  onClose,
  onPlayStart,
  onNext,
  onPrev,
  nextSchemaPosition,
  prevSchemaPosition,
}: {
  schema: Schema;
  myRole: MyRoleKey | null;
  onClose: () => void;
  onPlayStart?: (info: { trigger: "manual" | "auto"; startIdx: number }) => void;
  onNext?: () => void;
  onPrev?: () => void;
  nextSchemaPosition?: string | null;
  prevSchemaPosition?: string | null;
}) {
  const [localRole, setLocalRole] = useState<MyRoleKey | null>(initialRole);
  const [autoplay, setAutoplay] = useState(() => localStorage.getItem("schemaAutoplay") === "true");
  const [autoTrigger, setAutoTrigger] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= 768);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  const mobileWidth = Math.max(320, Math.min(viewportWidth - 20, 560));
  const CW = isMobileView ? mobileWidth : 380,
    CH = isMobileView ? Math.round(mobileWidth * 1.18) : 500;

  useEffect(() => {
    const onResize = () => {
      setIsMobileView(window.innerWidth <= 768);
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const steps = useMemo<Step[]>(() => {
    if (schema.steps && schema.steps.length >= 2) return schema.steps;

    const subs = schema.playerCount === 6 ? SUB_6 : SUB_4;
    const subsPos: Substitutes = {};
    subs.forEach((id) => {
      subsPos[id] = DEF_SUB[id];
    });

    return [
      { label: "Réglementaire", positions: schema.base, substitutes: { ...subsPos } },
      { label: "Jeu", positions: schema.perm, substitutes: { ...subsPos } },
    ];
  }, [schema.base, schema.perm, schema.playerCount, schema.steps]);

  const ids = useMemo(() => Object.keys(steps[0]?.positions ?? {}) as PlayerKey[], [steps]);

  const r4MaxInstances = useMemo(() => {
    let max = 0;
    for (const step of steps) {
      const roles = (step as any)?.roles as RoleMap | undefined;
      const count = ids.filter((pid) => (roles?.[pid] ?? pid) === "R4").length;
      if (count > max) max = count;
    }
    return max;
  }, [ids, steps]);

  useEffect(() => {
    // Si on passe sur un schéma où il peut y avoir 2 R4, éviter une sélection "R4" qui ne matche plus.
    if (localRole === "R4" && r4MaxInstances >= 2) setLocalRole("R4_1");
  }, [localRole, r4MaxInstances]);

  const localRoleBase = localRole ? baseRoleOfMyRole(localRole) : null;
  const localRoleDisplay =
    localRole === "R4_1"
      ? "R4 1"
      : localRole === "R4_2"
        ? "R4 2"
        : localRole
          ? isSubstituteRole(localRole)
            ? shortRoleCode(SUB_PC[localRole].baseRole)
            : shortRoleCode(localRole)
          : "";

  const instruct = localRoleBase && schema.base[localRoleBase] ? roleInstructions(localRoleBase, schema) : null;

  const basePlayers = schema.playerCount === 6 ? P6 : P4;
  const baseSubs = schema.playerCount === 6 ? SUB_6 : SUB_4;
  const roleOptions = useMemo(() => {
    const opts: Array<{ key: MyRoleKey; display: string; title: string; bg: string }> = [];

    for (const id of basePlayers) {
      if (id === "R4" && r4MaxInstances >= 2) {
        opts.push({ key: "R4_1", display: "R4 1", title: PC.R4.label, bg: PC.R4.bg });
        opts.push({ key: "R4_2", display: "R4 2", title: PC.R4.label, bg: PC.R4.bg });
        continue;
      }

      opts.push({ key: id, display: shortRoleCode(id), title: PC[id].label, bg: PC[id].bg });
    }

    for (const sid of baseSubs) {
      const baseRole = SUB_PC[sid].baseRole;
      opts.push({ key: sid, display: shortRoleCode(baseRole), title: SUB_PC[sid].label, bg: PC[baseRole].bg });
    }

    return opts;
  }, [basePlayers, baseSubs, r4MaxInstances]);

  useEffect(() => {
    setAutoTrigger(false);
    if (!autoplay) return;
    
    const timer = setTimeout(() => {
      setAutoTrigger(true);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [autoplay, schema.id]);

  const toggleAutoplay = () => {
    const next = !autoplay;
    setAutoplay(next);
    localStorage.setItem("schemaAutoplay", String(next));
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,.75)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: isMobileView ? "stretch" : "center",
        justifyContent: isMobileView ? "stretch" : "center",
        padding: isMobileView ? 0 : 16,
        overflowY: isMobileView ? "hidden" : "auto",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: isMobileView ? 0 : 16,
          boxShadow: "0 20px 60px rgba(0,0,0,.3)",
          width: "100%",
          maxWidth: isMobileView ? "100vw" : 600,
          maxHeight: isMobileView ? "100dvh" : "90vh",
          height: isMobileView ? "100dvh" : "auto",
          overflowY: "auto",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "#fff",
            borderBottom: "1px solid #e5e7eb",
            padding: isMobileView ? "8px 10px" : "12px 16px",
            borderTopLeftRadius: isMobileView ? 0 : 16,
            borderTopRightRadius: isMobileView ? 0 : 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: isMobileView ? 6 : 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: isMobileView ? 15 : 18, fontWeight: 700, color: "#111827" }}>{schema.name}</div>
              <TypePill type={schema.type} />
            </div>
            <button
              onClick={onClose}
              style={{
                width: isMobileView ? 30 : 36,
                height: isMobileView ? 30 : 36,
                borderRadius: isMobileView ? 8 : 10,
                border: "1px solid #e5e7eb",
                background: "#f9fafb",
                cursor: "pointer",
                fontSize: isMobileView ? 15 : 18,
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: isMobileView ? 6 : 10, flexWrap: "wrap" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: isMobileView ? 4 : 6,
                background: "#f3f4f6",
                borderRadius: 10,
                padding: isMobileView ? "4px 8px" : "6px 10px",
                border: "1px solid #e5e7eb",
                flex: 1,
                minWidth: 0,
              }}
            >
              <span style={{ fontSize: 10, color: "#6b7280", fontWeight: 600, whiteSpace: "nowrap", display: isMobileView ? "none" : "inline" }}>Je suis</span>
              <div
                style={{
                  display: "flex",
                  gap: 3,
                  flexWrap: isMobileView ? "nowrap" : "wrap",
                  overflowX: isMobileView ? "auto" : "visible",
                  flex: 1,
                }}
              >
                {roleOptions.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setLocalRole((r) => (r === opt.key ? null : opt.key))}
                    title={opt.title}
                    style={{
                      width: isMobileView ? 28 : 32,
                      height: isMobileView ? 24 : 28,
                      borderRadius: isMobileView ? 5 : 6,
                      border:
                        localRole === opt.key
                          ? "none"
                          : isSubstituteRole(opt.key)
                            ? `1.5px dashed ${opt.bg}66`
                            : `1.5px solid ${opt.bg}66`,
                      cursor: "pointer",
                      background: localRole === opt.key ? opt.bg : `${opt.bg}22`,
                      color: localRole === opt.key ? "#fff" : opt.bg,
                      fontSize: isMobileView ? 8 : 9,
                      fontWeight: 700,
                      fontFamily: "monospace",
                      boxShadow: localRole === opt.key ? `0 0 0 2px ${opt.bg},0 0 0 4px rgba(0,0,0,.1)` : "none",
                      transition: "all .15s",
                      transform: localRole === opt.key ? "scale(1.05)" : "scale(1)",
                      flexShrink: 0,
                    }}
                  >
                    {opt.display}
                  </button>
                ))}
              </div>
              {localRole && (
                <button
                  onClick={() => setLocalRole(null)}
                  style={{
                    width: isMobileView ? 18 : 20,
                    height: isMobileView ? 18 : 20,
                    borderRadius: "50%",
                    border: "none",
                    background: "rgba(0,0,0,.15)",
                    color: "#fff",
                    fontSize: isMobileView ? 9 : 10,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: isMobileView ? "4px 8px" : "6px 12px",
                borderRadius: isMobileView ? 8 : 10,
                border: "1px solid #e5e7eb",
                background: autoplay ? "#dcfce7" : "#f3f4f6",
                cursor: "pointer",
                fontSize: isMobileView ? 10 : 11,
                fontWeight: 700,
                color: autoplay ? "#166534" : "#6b7280",
                transition: "all .2s",
                flexShrink: 0,
              }}
            >
              <input type="checkbox" checked={autoplay} onChange={toggleAutoplay} style={{ width: isMobileView ? 12 : 14, height: isMobileView ? 12 : 14, cursor: "pointer" }} />
              <span style={{ whiteSpace: "nowrap" }}>Autoplay</span>
            </label>
          </div>
        </div>

        <div style={{ padding: isMobileView ? "10px" : "16px", display: "flex", flexDirection: "column", gap: isMobileView ? 8 : 12, alignItems: "center" }}>
          {localRole && (
            <div
              style={{
                width: "100%",
                maxWidth: CW,
                borderRadius: 10,
                overflow: "hidden",
                border: `1px solid ${instruct ? instruct.color + "44" : "#e5e7eb"}`,
                boxShadow: `0 2px 12px ${instruct ? instruct.color + "22" : "rgba(0,0,0,.05)"}`,
              }}
            >
              {instruct ? (
                <div style={{ padding: "12px 16px", background: `${instruct.color}0d` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        background: localRoleBase ? PC[localRoleBase].bg : "#111827",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#fff",
                        fontFamily: "monospace",
                        flexShrink: 0,
                      }}
                    >
                      {localRoleDisplay}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: instruct.color }}>{instruct.action}</div>
                      <div style={{ fontSize: 10, color: "#6b7280" }}>{localRoleBase ? PC[localRoleBase].label : ""}</div>
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#374151",
                      lineHeight: 1.55,
                      background: "rgba(255,255,255,.7)",
                      borderRadius: 8,
                      padding: "8px 12px",
                      border: `1px solid ${instruct.color}33`,
                    }}
                  >
                    {instruct.detail}
                  </div>
                </div>
              ) : (
                <div style={{ padding: "12px 16px", background: "#fff8f8", textAlign: "center", fontSize: 12, color: "#9ca3af" }}>
                  ⚠️ <strong>{localRoleDisplay}</strong> n'est pas dans ce schéma ({schema.playerCount} joueuses)
                </div>
              )}
            </div>
          )}

          <AniCourt
            schema={schema}
            w={CW}
            h={CH}
            myRole={localRole}
            autoTrigger={autoTrigger}
            onAutoTriggered={() => setAutoTrigger(false)}
            onPlayStart={onPlayStart}
          />

          <div style={{ position: "relative", width: "100%", display: "flex", justifyContent: "center", alignItems: "center", marginTop: 16 }}>
            <button
              onClick={onPrev}
              disabled={!onPrev}
              style={{
                position: isMobileView ? "static" : "absolute",
                left: isMobileView ? "auto" : 0,
                padding: isMobileView ? "8px 14px" : "12px 20px",
                borderRadius: 10,
                border: "2px solid #2563eb",
                background: onPrev ? "linear-gradient(135deg, #3b82f6, #2563eb)" : "#e0e7ff",
                color: onPrev ? "#fff" : "#818cf8",
                fontSize: isMobileView ? 12 : 14,
                fontWeight: 800,
                cursor: onPrev ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "all .2s",
                opacity: onPrev ? 1 : 0.4,
                boxShadow: onPrev ? "0 4px 12px rgba(37, 99, 235, 0.3)" : "none",
                marginRight: isMobileView ? 4 : 0,
              }}
              onMouseEnter={(e) => {
                if (onPrev && !isMobileView) {
                  e.currentTarget.style.background = "linear-gradient(135deg, #1e40af, #1e3a8a)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(37, 99, 235, 0.5)";
                  e.currentTarget.style.transform = "scale(1.08)";
                }
              }}
              onMouseLeave={(e) => {
                if (onPrev && !isMobileView) {
                  e.currentTarget.style.background = "linear-gradient(135deg, #3b82f6, #2563eb)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(37, 99, 235, 0.3)";
                  e.currentTarget.style.transform = "scale(1)";
                }
              }}
            >
              ← {prevSchemaPosition || "Précédent"}
            </button>

            <button
              onClick={onNext}
              disabled={!onNext}
              style={{
                position: isMobileView ? "static" : "absolute",
                right: isMobileView ? "auto" : 0,
                padding: isMobileView ? "8px 14px" : "12px 20px",
                borderRadius: 10,
                border: "2px solid #2563eb",
                background: onNext ? "linear-gradient(135deg, #3b82f6, #2563eb)" : "#e0e7ff",
                color: onNext ? "#fff" : "#818cf8",
                fontSize: isMobileView ? 12 : 14,
                fontWeight: 800,
                cursor: onNext ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "all .2s",
                opacity: onNext ? 1 : 0.4,
                boxShadow: onNext ? "0 4px 12px rgba(37, 99, 235, 0.3)" : "none",
                marginLeft: isMobileView ? 4 : 0,
              }}
              onMouseEnter={(e) => {
                if (onNext && !isMobileView) {
                  e.currentTarget.style.background = "linear-gradient(135deg, #1e40af, #1e3a8a)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(37, 99, 235, 0.5)";
                  e.currentTarget.style.transform = "scale(1.08)";
                }
              }}
              onMouseLeave={(e) => {
                if (onNext && !isMobileView) {
                  e.currentTarget.style.background = "linear-gradient(135deg, #3b82f6, #2563eb)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(37, 99, 235, 0.3)";
                  e.currentTarget.style.transform = "scale(1)";
                }
              }}
            >
              {nextSchemaPosition || "Suivant"} →
            </button>
          </div>

          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", justifyContent: "center", width: "100%", maxWidth: CW }}>
            {Object.keys(schema.base).map((id) => {
              const pid = id as PlayerKey;
              return (
                <span
                  key={pid}
                  onClick={() => setLocalRole((r) => (r === pid ? null : pid))}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "4px 10px",
                    borderRadius: 20,
                    cursor: "pointer",
                    background: localRole === pid ? PC[pid].bg + "22" : "#f3f4f6",
                    border: `1px solid ${localRole === pid ? PC[pid].bg + "55" : "#e5e7eb"}`,
                    boxShadow: localRole === pid ? `0 0 8px ${PC[pid].bg}44` : "none",
                    transition: "all .15s",
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: PC[pid].bg, flexShrink: 0, boxShadow: localRole === pid ? `0 0 6px ${PC[pid].bg}` : "none" }} />
                  <span style={{ fontWeight: 700, color: PC[pid].bg, fontSize: 10, fontFamily: "monospace" }}>{shortRoleCode(pid)}</span>
                  <span style={{ color: "#6b7280", fontSize: 10 }}>{PC[pid].label}</span>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewPanel({
  schemas,
  tacticLabel,
  onSchemaClick,
  onEdit,
  onDelete,
  onDuplicate,
  isAdmin,
}: {
  schemas: Schema[];
  tacticLabel: (s: Schema) => string;
  onSchemaClick: (s: Schema) => void;
  onEdit: (s: Schema) => void;
  onDelete: (id: string) => void;
  onDuplicate: (s: Schema) => void;
  isAdmin: boolean;
}) {
  if (schemas.length === 0)
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#9ca3af", gap: 8, padding: 24 }}>
        <span style={{ fontSize: 40 }}>📋</span>
        <span style={{ fontSize: 13 }}>Aucun schéma dans cette catégorie</span>
      </div>
    );

  return (
    <div
      style={{
        padding: "16px",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
        gap: 12,
        alignContent: "start",
        overflowY: "auto",
        height: "100%",
      }}
    >
      {schemas.map((s) => {
        const tactic = tacticLabel(s);
        return (
          <div
            key={s.id}
            onClick={() => onSchemaClick(s)}
            style={{
              background: "#fff",
              borderRadius: 12,
              border: "1.5px solid #e5e7eb",
              padding: "12px 14px",
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,.06)",
              transition: "all .15s",
              position: "relative",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#3b82f6";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(59,130,246,.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e5e7eb";
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,.06)";
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #10b981, #059669)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(16,185,129,.3)",
                zIndex: 1,
              }}
            >
              <span style={{ fontSize: 12, color: "#fff" }}>▶</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span style={{ fontSize: 24, flexShrink: 0, lineHeight: 1 }}>{s.type === "service" ? "⚡" : "🛡"}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#111827",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.3,
                  }}
                >
                  {s.name}
                </div>
                <div style={{ display: "flex", gap: 5, marginTop: 4, alignItems: "center", flexWrap: "wrap" }}>
                  <TypePill type={s.type} />
                  {tactic ? (
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: 20,
                        fontSize: 10,
                        fontWeight: 700,
                        background: "#f3f4f6",
                        color: "#374151",
                        border: "1px solid #e5e7eb",
                        maxWidth: 140,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={tactic}
                    >
                      📁 {tactic}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
              {isAdmin && (
                <button
                  onClick={() => onEdit(s)}
                  title="Modifier"
                  style={{
                    padding: "6px 10px",
                    borderRadius: 7,
                    border: "1px solid #e5e7eb",
                    background: "#f9fafb",
                    color: "#6b7280",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  ✏️ Modifier
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => onDuplicate(s)}
                  title="Dupliquer"
                  style={{
                    padding: "6px 10px",
                    borderRadius: 7,
                    border: "1px solid #bfdbfe",
                    background: "#eff6ff",
                    color: "#2563eb",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  📄 Dupliquer
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => onDelete(s.id)}
                  title="Supprimer"
                  style={{
                    padding: "6px 10px",
                    borderRadius: 7,
                    border: "1px solid #fecaca",
                    background: "#fff",
                    color: "#dc2626",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  🗑 Supprimer
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const DEF: Record<PlayerKey, Pos> = {
  Pass: { nx: 0.85, ny: 0.85 },
  Poi: { nx: MX + 0.18 * LW, ny: MY + 0.22 * LH },
  Lib: { nx: MX + 0.18 * LW, ny: MY + 0.68 * LH },
  R4: { nx: 0.5, ny: MY + 0.68 * LH },
  C1: { nx: 0.5, ny: MY + 0.22 * LH },
  C2: { nx: MX + 0.82 * LW, ny: MY + 0.22 * LH },
};

// Positions par défaut des remplaçants sur le banc
const DEF_SUB: Record<SubstituteKey, Pos> = {
  Sub_Pass: { nx: BENCH_START_X + BENCH_SPACING_X * 0, ny: BENCH_Y },
  Sub_Poi: { nx: BENCH_START_X + BENCH_SPACING_X * 1, ny: BENCH_Y },
  Sub_Lib: { nx: BENCH_START_X + BENCH_SPACING_X * 2, ny: BENCH_Y },
  Sub_R4: { nx: BENCH_START_X + BENCH_SPACING_X * 3, ny: BENCH_Y },
  Sub_C1: { nx: BENCH_START_X + BENCH_SPACING_X * 4, ny: BENCH_Y },
  Sub_C2: { nx: BENCH_START_X + BENCH_SPACING_X * 5, ny: BENCH_Y },
};

function CreatorPanel({
  initial,
  onSave,
  onCancel,
  tactics,
}: {
  initial: Schema | null;
  onSave: (schema: Schema) => void;
  onCancel: () => void;
  tactics?: SchemaTactic[];
}) {
  // En édition, on doit pouvoir modifier les infos (dont tactique), donc on démarre sur "meta".
  const [step, setStep] = useState<"meta" | "steps">("meta");
  const [name, setName] = useState(initial?.name || "");
  const [type, setType] = useState<SchemaType>(initial?.type || "reception");
  const [count, setCount] = useState<4 | 6>(coercePlayerCount(initial?.playerCount ?? 6));
  const [tactic, setTactic] = useState(initial?.tactic || "");
  const [tacticId, setTacticId] = useState<number | null>(initial?.tacticId ?? null);
  const [sel, setSel] = useState<PlayerKey | null>(null);
  const [subSel, setSubSel] = useState<SubstituteKey | null>(null);
  const [ballSel, setBallSel] = useState(false);

  const tacticsByName = useMemo(() => {
    const map = new Map<string, number>();
    (tactics || []).forEach((t) => {
      const k = String(t?.name ?? "").trim().toLowerCase();
      if (!k) return;
      map.set(k, Number(t.id));
    });
    return map;
  }, [tactics]);

  const tacticsById = useMemo(() => {
    const map = new Map<number, string>();
    (tactics || []).forEach((t) => {
      const id = Number(t.id);
      if (!Number.isFinite(id)) return;
      map.set(id, String(t?.name ?? "").trim());
    });
    return map;
  }, [tactics]);

  useEffect(() => {
    if (tacticId != null) return;
    const k = tactic.trim().toLowerCase();
    if (!k) return;
    const id = tacticsByName.get(k);
    if (id != null && Number.isFinite(Number(id))) setTacticId(Number(id));
  }, [tactic, tacticId, tacticsByName]);

  const CW = 340,
    CH = 460;
  const players = count === 6 ? P6 : P4;
  const subs = count === 6 ? SUB_6 : SUB_4;

  const initSteps = useCallback((): Step[] => {
    if (initial?.steps)
      return initial.steps.map((s) => ({
        label: s.label,
        positions: { ...s.positions },
        roles: (s as any).roles ? { ...(s as any).roles } : undefined,
        substitutes: s.substitutes ? { ...s.substitutes } : undefined,
        ...(s.quiz === true ? { quiz: true } : {}),
        ...(s.ball
          ? {
              ball: {
                nx: (s.ball as any).nx ?? 0.5,
                ny: (s.ball as any).ny ?? 0.35,
              },
            }
          : {}),
      }));

    const pos: Positions = {};
    players.forEach((id) => {
      pos[id] = DEF[id] || { nx: 0.5, ny: 0.75 };
    });
    
    const subsPos: Substitutes = {};
    subs.forEach((id) => {
      subsPos[id] = DEF_SUB[id];
    });
    
    return [
      { label: "Réglementaire", positions: { ...pos }, substitutes: { ...subsPos } },
      { label: "Jeu", positions: { ...pos }, substitutes: { ...subsPos } },
    ];
  }, [initial, players, subs]);

  const [steps, setSteps] = useState<Step[]>(initSteps);
  const [curStep, setCurStep] = useState(0);

  // Ensure step definitions follow player count changes
  useEffect(() => {
    setSteps((prev) => {
      const ids = players;
      const subIds = subs;
      const out = prev.map((s) => {
        const nextPos: Positions = {};
        ids.forEach((id) => {
          nextPos[id] = s.positions[id] || DEF[id] || { nx: 0.5, ny: 0.75 };
        });

        const nextRoles: RoleMap = {};
        const curRoles = (s as any).roles as RoleMap | undefined;
        ids.forEach((id) => {
          const r = curRoles?.[id];
          if (r) nextRoles[id] = r;
        });
        
        const nextSubs: Substitutes = {};
        subIds.forEach((id) => {
          nextSubs[id] = (s.substitutes && s.substitutes[id]) || DEF_SUB[id];
        });
        
        const next: Step = { ...s, positions: nextPos, substitutes: nextSubs, roles: Object.keys(nextRoles).length ? nextRoles : undefined };
        if (s.ball) next.ball = { nx: (s.ball as any).nx ?? 0.5, ny: (s.ball as any).ny ?? 0.35 };
        return next;
      });
      return out;
    });
    setCurStep((c) => Math.min(c, steps.length - 1));
  }, [count, players, subs, steps.length]);

  const goSteps = () => {
    if (!name.trim()) return;
    setStep("steps");
  };

  const doSave = () => {
    const schema: Schema = {
      id: initial?.id || Date.now().toString(),
      name: name.trim(),
      type,
      playerCount: count,
      tactic: tactic.trim(),
      tacticId,
      steps,
      base: steps[0].positions,
      perm: steps[steps.length - 1].positions,
      createdAt: initial?.createdAt || Date.now(),
    };
    onSave(schema);
  };

  const mvPlayer = useCallback(
    (id: PlayerKey, p: Pos) => {
      setSteps((ss) => {
        const ns = [...ss];
        ns[curStep] = { ...ns[curStep], positions: { ...ns[curStep].positions, [id]: p } };
        return ns;
      });
    },
    [curStep]
  );

  const mvSub = useCallback(
    (id: SubstituteKey, p: Pos) => {
      setSteps((ss) => {
        const ns = [...ss];
        const cur = ns[curStep];
        const curSubs = cur.substitutes || {};
        ns[curStep] = { ...cur, substitutes: { ...curSubs, [id]: p } };
        return ns;
      });
    },
    [curStep]
  );

  const changeRole = useCallback(
    (playerId: PlayerKey, newRole: PlayerKey) => {
      setSteps((ss) => {
        const ns = [...ss];
        const cur = ns[curStep];
        const curRoles = ((cur as any).roles as RoleMap | undefined) || {};
        const oldRole = curRoles[playerId] || playerId;
        if (oldRole === newRole) return ss;

        const nextRoles: RoleMap = { ...curRoles, [playerId]: newRole };
        // Ne touche pas aux positions: aucune autre pastille n'est impactée.
        ns[curStep] = { ...cur, roles: nextRoles };
        return ns;
      });
    },
    [curStep]
  );

  const toggleBall = () => {
    setSteps((ss) => {
      const ns = [...ss];
      const cur = ns[curStep];
      if (cur.ball) {
        const { ball, ...rest } = cur;
        ns[curStep] = rest;
      } else {
        ns[curStep] = { ...cur, ball: { nx: 0.5, ny: 0.35 } };
      }
      return ns;
    });
    setBallSel(true);
  };

  const moveBall = useCallback(
    (b: BallPos) => {
      setSteps((ss) => {
        const ns = [...ss];
        ns[curStep] = { ...ns[curStep], ball: b };
        return ns;
      });
    },
    [curStep]
  );

  // Marque l'étape i comme étape du quiz (une seule par schéma). Re-cliquer la retire :
  // le quiz retombe alors sur l'« Étape 1 » par défaut.
  const toggleQuizStep = (i: number) => {
    setSteps((ss) =>
      ss.map((s, idx) => {
        const flagged = idx === i && s.quiz !== true;
        if (flagged) return { ...s, quiz: true };
        if (s.quiz) {
          const { quiz, ...rest } = s;
          return rest;
        }
        return s;
      })
    );
  };

  const addStep = () => {
    setSteps((ss) => {
      const curPositions = { ...ss[curStep].positions };
      const curSubs = ss[curStep].substitutes ? { ...ss[curStep].substitutes } : undefined;
      const curRoles = (ss[curStep] as any).roles ? { ...(ss[curStep] as any).roles } : undefined;
      const b = ss[curStep].ball;
      const curBall: BallPos | undefined = b ? { nx: (b as any).nx ?? 0.5, ny: (b as any).ny ?? 0.35 } : undefined;
      const newStep: Step = {
        label: `Étape ${ss.length - 1}`,
        positions: curPositions,
        roles: curRoles,
        substitutes: curSubs,
        ...(curBall ? { ball: curBall } : {}),
      };

      const ns = [...ss];
      ns.splice(ss.length - 1, 0, newStep);
      return ns;
    });
    setCurStep(curStep + 1);
  };

  const removeStep = (i: number) => {
    if (steps.length <= 2) return;
    setSteps((ss) => ss.filter((_, idx) => idx !== i));
    setCurStep((c) => Math.min(c, steps.length - 2));
  };

  const copyFromPrev = (i: number) => {
    if (i === 0) return;
    setSteps((ss) => {
      const ns = [...ss];
      ns[i] = {
        ...ns[i],
        positions: { ...ss[i - 1].positions },
        roles: (ss[i - 1] as any).roles ? { ...(ss[i - 1] as any).roles } : undefined,
        substitutes: ss[i - 1].substitutes ? { ...ss[i - 1].substitutes } : undefined,
        ball: ss[i - 1].ball,
      };
      return ns;
    });
  };

  if (step === "meta") {
    return (
      <div style={{ padding: "20px", maxWidth: 480, color: "#111827" }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: "0 0 16px" }}>{initial ? "Modifier" : "Nouveau schéma"}</h3>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 5 }}>Nom</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && name.trim()) goSteps();
            }}
            placeholder="ex : P1 Réception…"
            style={{
              width: "100%",
              padding: "9px 12px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              fontSize: 13,
              fontFamily: "inherit",
              outline: "none",
              background: "#f9fafb",
              color: "#111827",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 5 }}>Tactique</label>
          {tactics && tactics.length > 0 ? (
            <select
              value={tacticId == null ? "" : String(tacticId)}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  setTacticId(null);
                  setTactic("");
                  return;
                }
                const id = Number(v);
                setTacticId(Number.isFinite(id) ? id : null);
                setTactic(String(tacticsById.get(id) || "").trim());
              }}
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                fontSize: 13,
                fontFamily: "inherit",
                outline: "none",
                background: "#f9fafb",
                color: "#111827",
                boxSizing: "border-box",
              }}
            >
              <option value="">Sans tactique</option>
              {tactics.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </select>
          ) : (
            <input
              value={tactic}
              onChange={(e) => setTactic(e.target.value)}
              placeholder="ex : 'Système', 'Bloc', 'Réception'…"
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                fontSize: 13,
                fontFamily: "inherit",
                outline: "none",
                background: "#f9fafb",
                color: "#111827",
                boxSizing: "border-box",
              }}
            />
          )}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>Type</label>
          <div style={{ display: "flex", gap: 8 }}>
            {([
              ["reception", "🛡 Réception", "#dbeafe", "#1e3a8a", "#93c5fd"],
              ["service", "⚡ Service", "#fef9c3", "#713f12", "#fcd34d"],
            ] as const).map(([v, l, bg, tc, bc]) => (
              <button
                key={v}
                onClick={() => setType(v)}
                style={{
                  flex: 1,
                  padding: "9px",
                  borderRadius: 8,
                  border: `2px solid ${type === v ? bc : "#e5e7eb"}`,
                  background: type === v ? bg : "#f9fafb",
                  color: type === v ? tc : "#9ca3af",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>Joueuses</label>
          <div style={{ display: "flex", gap: 8 }}>
            {([
              [6, "6 joueuses"],
              [4, "4 joueuses (M13)"],
            ] as const).map(([n, l]) => (
              <button
                key={n}
                onClick={() => setCount(n)}
                style={{
                  flex: 1,
                  padding: "9px",
                  borderRadius: 8,
                  border: `2px solid ${count === n ? "#2563eb" : "#e5e7eb"}`,
                  background: count === n ? "#eff6ff" : "#f9fafb",
                  color: count === n ? "#1d4ed8" : "#9ca3af",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <SmBtn onClick={goSteps} disabled={!name.trim()} bg="#2563eb">
            Suivant →
          </SmBtn>
          <SmBtn onClick={onCancel} outline>
            Annuler
          </SmBtn>
        </div>
      </div>
    );
  }

  const curPos = steps[curStep]?.positions || {};
  const curSubs = steps[curStep]?.substitutes || {};
  const curRoles = (steps[curStep] as any)?.roles as RoleMap | undefined;
  const regEditorPos = steps[0]?.positions || {};
  const regEditorSubs = steps[0]?.substitutes || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "10px 12px" }}>
      <div style={{ width: "100%", maxWidth: 380, display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center", maxHeight: 80, overflowY: "auto" }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <button
                onClick={() => setCurStep(i)}
                style={{
                  padding: "4px 11px",
                  borderRadius: 7,
                  border: "1.5px solid",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  borderColor: curStep === i ? "#2563eb" : "#e5e7eb",
                  background: curStep === i ? "#2563eb" : i < steps.length - 1 ? "#f9fafb" : "#fff0fd",
                  color: curStep === i ? "#fff" : i === 0 ? "#374151" : i === steps.length - 1 ? "#7c3aed" : "#374151",
                }}
              >
                {i === 0 ? "① Réglementaire" : i === steps.length - 1 ? "⑤ Pos. finale" : `② Étape ${i}`}
                {s.quiz === true && " 🎯"}
              </button>
              {i > 0 && i < steps.length - 1 && (
                <button
                  onClick={() => removeStep(i)}
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: "1px solid #fecaca",
                    background: "#fff",
                    color: "#dc2626",
                    fontSize: 9,
                    cursor: "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ✕
                </button>
              )}
              {i < steps.length - 1 && <span style={{ color: "#d1d5db", fontSize: 10 }}>→</span>}
              {/* Bouton + Étape avant le dernier élément */}
              {i === steps.length - 2 && steps.length < 10 && (
                <>
                  <span style={{ color: "#d1d5db", fontSize: 10 }}>→</span>
                  <button
                    onClick={addStep}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 7,
                      border: "1.5px dashed #93c5fd",
                      background: "#eff6ff",
                      color: "#2563eb",
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    + Étape
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ flex: 1, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 7, padding: "5px 10px", fontSize: 11, color: "#2563eb" }}>
            {curStep === 0 ? "📌 Position réglementaire — glissez les joueuses" : curStep === steps.length - 1 ? "🎯 Position finale de jeu" : `↔️ Étape intermédiaire ${curStep}`}
          </div>
          {curStep > 0 && (
            <SmBtn onClick={() => copyFromPrev(curStep)} bg="#f59e0b">
              ↩ Copier préc.
            </SmBtn>
          )}
        </div>
      </div>

      {(() => {
        const curBall = steps[curStep]?.ball;
        const isQuizStep = steps[curStep]?.quiz === true;
        return (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", width: "100%", maxWidth: 380 }}>
            <button
              onClick={toggleBall}
              style={{
                padding: "5px 12px",
                borderRadius: 7,
                border: `1.5px solid ${curBall ? "#3b82f6" : "#d1d5db"}`,
                background: curBall ? "#3b82f622" : "#f3f4f6",
                color: curBall ? "#3b82f6" : "#6b7280",
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              🏐 {curBall ? "Retirer le ballon" : "Ajouter un ballon"}
            </button>
            {/* L'étape 0 (Réglementaire) reste la référence des postes 1-6 : pas cochable. */}
            {curStep > 0 && (
              <button
                onClick={() => toggleQuizStep(curStep)}
                title="Le quiz portera sur cette étape (par défaut : Étape 1)"
                style={{
                  padding: "5px 12px",
                  borderRadius: 7,
                  border: `1.5px solid ${isQuizStep ? "#16a34a" : "#d1d5db"}`,
                  background: isQuizStep ? "#16a34a22" : "#f3f4f6",
                  color: isQuizStep ? "#16a34a" : "#6b7280",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                🎯 {isQuizStep ? "Étape du quiz ✓" : "Étape du quiz"}
              </button>
            )}
          </div>
        );
      })()}

      {/* Affichage des changements de poste (identité -> rôle affiché) */}
      {(() => {
        const changes = players
          .map((pid) => {
            const fromRole = pid;
            const toRole = curRoles?.[pid] || pid;
            if (toRole === fromRole) return null;
            const p = curPos[pid];
            const zone = p ? getZone(p) : "?";
            return {
              pid,
              fromRole,
              toRole,
              zone,
              fromLabel: PC[fromRole].label,
              toLabel: PC[toRole].label,
            };
          })
          .filter(Boolean) as Array<{ pid: PlayerKey; fromRole: PlayerKey; toRole: PlayerKey; zone: string; fromLabel: string; toLabel: string }>;

        if (changes.length === 0) return null;

        return (
          <div style={{ width: "100%", maxWidth: 380, marginTop: 6 }}>
            <div
              style={{
                background: "#fef3c7",
                border: "1.5px solid #fbbf24",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 11,
                color: "#78350f",
                fontWeight: 600,
                lineHeight: 1.5,
              }}
            >
              <div style={{ marginBottom: 4, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em" }}>
                🔄 Changements de poste :
              </div>
              {changes.map((c, i) => (
                <div key={i}>
                  La <strong>{c.fromLabel}</strong> joue en <strong>{c.toLabel}</strong> dans la <strong>{c.zone}</strong>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <Court w={CW} h={CH}>
        {players.map((id) => {
          const p = curPos[id] || DEF[id] || { nx: 0.5, ny: 0.75 };
          const role = curRoles?.[id] || id;
          const usedRoles = players.filter((x) => x !== id).map((x) => (curRoles?.[x] || x));
          return (
            <DragP
              key={id}
              id={id}
              role={role}
              nx={p.nx}
              ny={p.ny}
              zoneNy={regEditorPos[id]?.ny ?? p.ny}
              w={CW}
              h={CH}
              onMove={mvPlayer}
              sel={sel === id}
              onSel={() => {
                setSel(id);
                setSubSel(null);
                setBallSel(false);
              }}
              onRoleChange={changeRole}
              usedRoles={usedRoles}
            />
          );
        })}

        {subs.map((id) => {
          const p = curSubs[id] || DEF_SUB[id];
          return (
            <DragSub
              key={id}
              id={id}
              nx={p.nx}
              ny={p.ny}
              zoneNy={regEditorSubs[id]?.ny ?? p.ny}
              w={CW}
              h={CH}
              onMove={mvSub}
              sel={subSel === id}
              onSel={() => {
                setSel(null);
                setSubSel(id);
                setBallSel(false);
              }}
            />
          );
        })}

        {steps[curStep]?.ball && (
          <DragBall
            ball={steps[curStep].ball!}
            w={CW}
            h={CH}
            onMove={moveBall}
            sel={ballSel}
            onSel={() => {
              setSel(null);
              setSubSel(null);
              setBallSel(true);
            }}
          />
        )}
      </Court>

      {/* ===== DEBUG PANEL (mode édition) ===== */}
      {(() => {
        const seuilNet = MY + LH * 0.12;
        const seuil3m  = MY + LH * 0.42;
        const lines = players.map((id) => {
          const p = curPos[id] || DEF[id];
          if (!p) return `${id.padEnd(6)} — position introuvable`;
          const isAv = p.ny > seuilNet && p.ny < seuil3m;
          const isAr = p.ny >= seuil3m;
          const zone = isAv ? "AVANT  " : isAr ? "ARRIÈRE" : "HORS   ";
          const glow = isAv ? "#ff8800 (avant)" : isAr ? "#00d4b4 (arrière)" : "none";
          return `${id.padEnd(6)} ny=${p.ny.toFixed(3)} | ${zone} → glow=${glow}`;
        });
        const info = [
          `=== TERRAIN (étape ${curStep}) ===`,
          `MY=${MY}  LH=${LH}  seuil filet ny>${seuilNet.toFixed(3)}  seuil 3m ny>=${seuil3m.toFixed(3)}`,
          `Zone avant fill: rgba(255,140,30,.35)  |  Zone arrière fill: rgba(0,200,170,.30)`,
          ``,
          `=== JOUEURS (curPos) ===`,
          ...lines,
        ].join("\n");
        return (
          <pre style={{
            margin: "8px auto 0",
            maxWidth: CW,
            background: "#0f172a",
            color: "#a3e635",
            fontFamily: "monospace",
            fontSize: 10,
            padding: "10px 14px",
            borderRadius: 8,
            border: "1px solid #334155",
            whiteSpace: "pre",
            userSelect: "all",
          }}>
            {info}
          </pre>
        );
      })()}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
        {players.map((id) => {
          const p = curPos[id];
          return (
            <span
              key={id}
              style={{
                padding: "2px 7px",
                borderRadius: 20,
                fontSize: 9,
                background: PC[id].bg + "22",
                border: `1px solid ${PC[id].bg}55`,
                color: PC[id].bg,
                fontFamily: "monospace",
                fontWeight: 700,
              }}
            >
              {id}
              {p ? ` (${(p.nx * 100).toFixed(0)},${(p.ny * 100).toFixed(0)})` : ""}
            </span>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
        {curStep > 0 && (
          <SmBtn onClick={() => setCurStep((c) => c - 1)} outline>
            ← Préc.
          </SmBtn>
        )}
        {curStep < steps.length - 1 ? (
          <SmBtn onClick={() => setCurStep((c) => c + 1)} bg="#2563eb">
            Suivant →
          </SmBtn>
        ) : (
          <SmBtn onClick={doSave} bg="#16a34a">
            ✓ Enregistrer
          </SmBtn>
        )}
        <SmBtn onClick={() => setStep("meta")} outline>
          ‹ Nom
        </SmBtn>
      </div>
    </div>
  );
}

function TacticsModal({
  open,
  onClose,
  scope,
  tactics,
  setTactics,
  schemas,
  persist,
}: {
  open: boolean;
  onClose: () => void;
  scope: string;
  tactics: SchemaTactic[];
  setTactics: (t: SchemaTactic[]) => void;
  schemas: Schema[];
  persist: (u: Schema[]) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");

  useEffect(() => {
    if (!open) return;
    if (selectedId != null && tactics.some((t) => Number(t.id) === Number(selectedId))) return;
    setSelectedId(tactics[0]?.id ?? null);
  }, [open, selectedId, tactics]);

  const tacticsById = useMemo(() => new Map<number, string>(tactics.map((t) => [Number(t.id), String(t.name || "")])), [tactics]);
  const selectedName = selectedId != null ? String(tacticsById.get(Number(selectedId)) || "") : "";

  const counts = useMemo(() => {
    const map = new Map<number, number>();
    for (const s of schemas) {
      const id = s?.tacticId;
      if (id == null) continue;
      map.set(Number(id), (map.get(Number(id)) || 0) + 1);
    }
    return map;
  }, [schemas]);

  const refresh = useCallback(async () => {
    const t = await fetchSchemaTactics(scope);
    setTactics(t);
  }, [scope, setTactics]);

  const toggleSchema = (schemaId: string, checked: boolean) => {
    if (selectedId == null) return;
    const tid = Number(selectedId);
    const tname = selectedName.trim();
    const next = schemas.map((s) => {
      if (s.id !== schemaId) return s;
      if (checked) return { ...s, tacticId: tid, tactic: tname };
      if (Number(s.tacticId) !== tid) return s;
      return { ...s, tacticId: null, tactic: "" };
    });
    persist(next);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
      }}
    >
      <div
        style={{
          width: "min(980px, 100%)",
          maxHeight: "min(720px, 100%)",
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #e5e7eb",
          boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "12px 14px",
            background: "#f3f4f6",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase", color: "#111827" }}>Gestion des tactiques</div>
            <div style={{ fontSize: 10, color: "#6b7280", fontFamily: "monospace", marginTop: 2 }}>Affilier plusieurs schémas à une tactique</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => void refresh()}
              title="Rafraîchir"
              style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", fontWeight: 900 }}
            >
              ↻
            </button>
            <button
              onClick={onClose}
              title="Fermer"
              style={{ padding: "8px 12px", borderRadius: 10, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", fontWeight: 900 }}
            >
              ✕
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 0, flex: 1, minHeight: 0 }}>
          <div style={{ width: 320, borderRight: "1px solid #e5e7eb", padding: 12, overflowY: "auto" }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Nouvelle tactique…"
                style={{
                  flex: 1,
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  fontSize: 12,
                  fontFamily: "inherit",
                  outline: "none",
                  background: "#f9fafb",
                  color: "#111827",
                  boxSizing: "border-box",
                }}
              />
              <button
                disabled={busy || !newName.trim()}
                onClick={async () => {
                  const name = newName.trim();
                  if (!name) return;
                  try {
                    setBusy(true);
                    await createSchemaTactic(scope, name);
                    setNewName("");
                    await refresh();
                  } finally {
                    setBusy(false);
                  }
                }}
                style={{
                  padding: "8px 10px",
                  borderRadius: 10,
                  border: "none",
                  background: "#2563eb",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                }}
                title="Ajouter"
              >
                +
              </button>
            </div>

            {tactics.length === 0 ? <div style={{ fontSize: 12, color: "#6b7280" }}>Aucune tactique.</div> : null}

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {tactics.map((t) => {
                const id = Number(t.id);
                const isSelected = selectedId != null && Number(selectedId) === id;
                const isEditing = editId != null && Number(editId) === id;
                const count = counts.get(id) || 0;

                return (
                  <div
                    key={t.id}
                    onClick={() => setSelectedId(id)}
                    style={{
                      padding: "10px",
                      borderRadius: 12,
                      border: `1px solid ${isSelected ? "#93c5fd" : "#e5e7eb"}`,
                      background: isSelected ? "#eff6ff" : "#fff",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {isEditing ? (
                          <input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "7px 9px",
                              borderRadius: 10,
                              border: "1px solid #d1d5db",
                              fontSize: 12,
                              fontFamily: "inherit",
                              outline: "none",
                              background: "#f9fafb",
                              color: "#111827",
                              boxSizing: "border-box",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <div style={{ fontSize: 12, fontWeight: 900, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>📁 {t.name}</div>
                        )}
                        <div style={{ fontFamily: "monospace", fontSize: 10, color: "#6b7280", marginTop: 4 }}>{count} schéma{count > 1 ? "s" : ""}</div>
                      </div>

                      <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                        {isEditing ? (
                          <>
                            <button
                              disabled={busy}
                              onClick={async () => {
                                const name = editName.trim();
                                if (!name) return;
                                try {
                                  setBusy(true);
                                  await updateSchemaTactic(scope, id, name);
                                  setEditId(null);
                                  setEditName("");
                                  await refresh();
                                  persist(schemas.map((s) => (Number(s.tacticId) === id ? { ...s, tactic: name } : s)));
                                } finally {
                                  setBusy(false);
                                }
                              }}
                              style={{ padding: "7px 8px", borderRadius: 10, border: "none", background: "#16a34a", color: "#fff", cursor: "pointer", fontWeight: 900, fontSize: 12 }}
                              title="Enregistrer"
                            >
                              ✓
                            </button>
                            <button
                              disabled={busy}
                              onClick={() => {
                                setEditId(null);
                                setEditName("");
                              }}
                              style={{ padding: "7px 8px", borderRadius: 10, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", cursor: "pointer", fontWeight: 900, fontSize: 12 }}
                              title="Annuler"
                            >
                              ✕
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              disabled={busy}
                              onClick={() => {
                                setEditId(id);
                                setEditName(String(t.name || ""));
                              }}
                              style={{ padding: "7px 8px", borderRadius: 10, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", cursor: "pointer", fontWeight: 900, fontSize: 12 }}
                              title="Renommer"
                            >
                              ✏️
                            </button>
                            <button
                              disabled={busy}
                              onClick={async () => {
                                if (!window.confirm(`Supprimer la tactique "${t.name}" ?`)) return;
                                try {
                                  setBusy(true);
                                  await deleteSchemaTactic(scope, id);
                                  persist(schemas.map((s) => (Number(s.tacticId) === id ? { ...s, tacticId: null, tactic: "" } : s)));
                                  await refresh();
                                } finally {
                                  setBusy(false);
                                }
                              }}
                              style={{ padding: "7px 8px", borderRadius: 10, border: "none", background: "#dc2626", color: "#fff", cursor: "pointer", fontWeight: 900, fontSize: 12 }}
                              title="Supprimer"
                            >
                              🗑
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ flex: 1, padding: 12, overflowY: "auto" }}>
            {selectedId == null ? (
              <div style={{ padding: 16, color: "#6b7280", fontSize: 13 }}>Sélectionne une tactique à gauche.</div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: "#111827" }}>📁 {selectedName || "Tactique"}</div>
                  <div style={{ fontFamily: "monospace", fontSize: 11, color: "#6b7280" }}>{schemas.length} schéma{schemas.length > 1 ? "s" : ""}</div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {schemas.map((s) => {
                    const checked = Number(s.tacticId) === Number(selectedId);
                    return (
                      <label
                        key={s.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "10px 12px",
                          borderRadius: 12,
                          border: "1px solid #e5e7eb",
                          background: checked ? "#f0fdf4" : "#fff",
                          cursor: "pointer",
                        }}
                      >
                        <input type="checkbox" checked={checked} onChange={(e) => toggleSchema(s.id, e.target.checked)} style={{ width: 16, height: 16 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 900, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {s.type === "service" ? "⚡" : "🛡"} {s.name}
                            </div>
                            <div style={{ fontFamily: "monospace", fontSize: 11, color: "#6b7280" }}>{s.playerCount}v</div>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VolleySchemaStudio({ isAdmin }: VolleySchemaStudioProps) {
  const [schemas, setSchemas] = useState<Schema[]>([]);
  const scope = useMemo(() => getSchemasScope(), []);
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  // Accès à la révision : désactivable par un superadmin (les admins/coachs passent toujours).
  const [studioAccess, setStudioAccess] = useState<"checking" | "enabled" | "disabled">(isAdmin ? "enabled" : "checking");
  useEffect(() => {
    if (isAdmin) return;
    let cancelled = false;
    isSchemasEnabled()
      .then((enabled) => { if (!cancelled) setStudioAccess(enabled ? "enabled" : "disabled"); })
      .catch(() => { if (!cancelled) setStudioAccess("enabled"); });
    return () => { cancelled = true; };
  }, [isAdmin]);
  const [tactics, setTactics] = useState<SchemaTactic[]>([]);
  const didTacticMigration = useRef(false);
  const [tacticsOpen, setTacticsOpen] = useState(false);
  const [tacticFilter, setTacticFilter] = useState<string>("");

  const tacticsById = useMemo(() => new Map<number, string>(tactics.map((t) => [Number(t.id), String(t.name || "")])), [tactics]);
  const getTacticLabel = useCallback(
    (s: Schema) => {
      if (s?.tacticId != null) {
        const v = tacticsById.get(Number(s.tacticId));
        if (v && v.trim()) return v.trim();
      }
      return String(s?.tactic ?? "").trim();
    },
    [tacticsById]
  );

  const [dbSaving, setDbSaving] = useState(false);
  const [schemasLoading, setSchemasLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [tab, setTab] = useState<"6" | "4">("6");
  const [active, setActive] = useState<Schema | null>(null);
  const [view, setView] = useState<"review" | "create" | "edit">("review");
  const [editTarget, setEditTarget] = useState<Schema | null>(null);
  const [sideW, setSideW] = useState(240);
  const [myRole] = useState<PlayerKey | null>(null);
  const [isMobile] = useState(() => window.innerWidth <= 640);
  const [sideVisible, setSideVisible] = useState(() => window.innerWidth > 640);
  const [modalSchema, setModalSchema] = useState<Schema | null>(null);
  const [modalOpenedAt, setModalOpenedAt] = useState<number | null>(null);
  const lastSavedSchemas = useRef<Schema[]>([]);

  const tacticCountsById = useMemo(() => {
    const list =
      tab === "4"
        ? schemas.filter((s) => coercePlayerCount((s as any).playerCount) === 4)
        : schemas.filter((s) => coercePlayerCount((s as any).playerCount) === 6);

    const map = new Map<number, number>();
    for (const s of list) {
      const id = s?.tacticId;
      if (id == null) continue;
      const k = Number(id);
      if (!Number.isFinite(k)) continue;
      map.set(k, (map.get(k) || 0) + 1);
    }
    return map;
  }, [schemas, tab]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Migration best-effort: si l'ancien format (1 ligne JSON array) est présent, on le splitte en lignes.
        // Pas de suppression automatique de l'ancienne ligne pour éviter toute perte de données.
        if (isAdmin) {
          try {
            await migrateVolleySchemasToRows(scope);
          } catch {
            // ignore
          }
        }

        const remote = await fetchVolleySchemas(scope);
        if (cancelled) return;
        if (!remote) return;

        const next = uniqById(normalizeSchemas(remote));
        setSchemas(next);
        lastSavedSchemas.current = next;

        // Charger les tactiques
        try {
          const t = await fetchSchemaTactics(scope);
          if (!cancelled) setTactics(t);

          // Migration best-effort vers schema_tactics: si aucune tactique en base, mais des schémas ont un champ tactic (texte)
          if (isAdmin && !didTacticMigration.current && t.length === 0 && next.some((s) => !!String(s?.tactic ?? "").trim())) {
            didTacticMigration.current = true;
            try {
              await migrateSchemaTacticsFromSchemas(scope);
            } catch {
              // ignore
            }
            const [remote2, t2] = await Promise.all([fetchVolleySchemas(scope), fetchSchemaTactics(scope)]);
            if (!cancelled) {
              if (Array.isArray(remote2)) {
                const next2 = uniqById(normalizeSchemas(remote2));
                setSchemas(next2);
              }
              setTactics(Array.isArray(t2) ? t2 : []);
            }
          }
        } catch {
          // ignore
        }
      } catch {
        // Erreur silencieuse lors du chargement des schémas
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, scope]);

  const refreshSchemas = useCallback(async () => {
    setSchemasLoading(true);
    try {
      const remote = await fetchVolleySchemas(scope);
      if (remote) {
        const next = uniqById(normalizeSchemas(remote));
        setSchemas(next);
        lastSavedSchemas.current = next;
      }
      const t = await fetchSchemaTactics(scope);
      setTactics(t);
    } catch {
      // ignore
    } finally {
      setSchemasLoading(false);
    }
  }, [scope]);

  const refreshTactics = useCallback(async () => {
    try {
      const t = await fetchSchemaTactics(scope);
      setTactics(t);
    } catch {
      // ignore
    }
  }, [scope]);

  useEffect(() => {
    if (!tacticsOpen) return;
    void refreshTactics();
  }, [tacticsOpen, refreshTactics]);

  const closeSideOnMobile = () => {
    if (isMobile) setSideVisible(false);
  };

  const sideResizing = useRef(false);
  const sideStartX = useRef(0);
  const sideStartW = useRef(240);

  const onSideMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      sideResizing.current = true;
      sideStartX.current = e.clientX;
      sideStartW.current = sideW;

      const onMove = (ev: MouseEvent) => {
        if (!sideResizing.current) return;
        const delta = ev.clientX - sideStartX.current;
        setSideW(Math.max(160, Math.min(420, sideStartW.current + delta)));
      };
      const onUp = () => {
        sideResizing.current = false;
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [sideW]
  );

  const persist = (u: Schema[]) => {
    const next = uniqById(normalizeSchemas(u));
    setSchemas(next);
    setHasUnsavedChanges(true);

    if (isAdmin) {
      void upsertVolleySchemas(scope, next);
    }
  };

  const saveToDb = useCallback(async () => {
    if (!isAdmin) return;
    if (dbSaving) return;

    try {
      setDbSaving(true);
      await upsertVolleySchemas(scope, schemas);
      lastSavedSchemas.current = schemas;
      setHasUnsavedChanges(false);
      alert("Schémas enregistrés en base.");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      alert("Erreur d'enregistrement en base : " + msg);
    } finally {
      setDbSaving(false);
    }
  }, [dbSaving, isAdmin, schemas, scope]);

  const schemas6 = schemas.filter((s) => coercePlayerCount((s as any).playerCount) === 6);
  const schemas4 = schemas.filter((s) => coercePlayerCount((s as any).playerCount) === 4);
  const tabSchemas = tab === "4" ? sortSchemasForList(schemas4, tacticsById) : sortSchemasForList(schemas6, tacticsById);

  const filteredTabSchemas = useMemo(() => {
    if (!tacticFilter) return tabSchemas;
    if (tacticFilter === "__none__") return tabSchemas.filter((s) => !getTacticLabel(s));
    const id = Number(tacticFilter);
    if (Number.isFinite(id)) return tabSchemas.filter((s) => Number(s.tacticId) === id);
    return tabSchemas;
  }, [getTacticLabel, tabSchemas, tacticFilter]);

  const handleSave = (s: Schema) => {
    const updated = view === "edit" ? schemas.map((x) => (x.id === s.id ? s : x)) : [...schemas, s];
    persist(updated);
    setView("review");
    setEditTarget(null);
    setTab(s.playerCount === 4 ? "4" : "6");
    setActive(s);
  };

  const handleEdit = (s: Schema) => {
    setEditTarget(s);
    setView("edit");
    closeSideOnMobile();
  };

  const handleDelete = (id: string) => {
    if (!window.confirm("Supprimer ce schéma ?")) return;
    persist(schemas.filter((s) => s.id !== id));
    if (active?.id === id) setActive(null);
  };

  const handlePlay = (s: Schema) => {
    const now = Date.now();
    setModalOpenedAt(now);
    setModalSchema(s);
  };

  const handleModalPlayStart = (schema: Schema, info: { trigger: "manual" | "auto"; startIdx: number }) => {
    void trackActivityEvent({
      eventType: "schema_play",
      userId: currentUser?.id ?? null,
      username: currentUser?.username ?? null,
      role: currentUser?.role ?? null,
      tableName: "schemas",
      recordId: schema.id,
      schemaUid: schema.id,
      schemaName: schema.name,
      scope,
      origin: "schema_studio",
      success: true,
      metadata: {
        type: schema.type,
        playerCount: schema.playerCount,
        trigger: info.trigger,
        startIdx: info.startIdx,
      },
    }).catch(() => {
      // Tracking best-effort
    });
  };

  const handleDuplicate = (s: Schema) => {
    const copy: Schema = {
      ...(JSON.parse(JSON.stringify(s)) as Schema),
      id: Date.now().toString(),
      name: `${s.name} (copie)`,
      createdAt: Date.now(),
    };
    const updated = [...schemas, copy];
    persist(updated);
    setActive(copy);
    setTab(copy.playerCount === 4 ? "4" : "6");
  };

  const handleSelect = (s: Schema) => {
    handlePlay(s);
  };

  const handleCloseModal = () => {
    const openedAt = modalOpenedAt;
    const schema = modalSchema;
    setModalSchema(null);
    setModalOpenedAt(null);

    if (!schema || openedAt == null) return;

    const durationMs = Math.max(0, Date.now() - openedAt);
    void trackActivityEvent({
      eventType: "schema_play_end",
      userId: currentUser?.id ?? null,
      username: currentUser?.username ?? null,
      role: currentUser?.role ?? null,
      tableName: "schemas",
      recordId: schema.id,
      schemaUid: schema.id,
      schemaName: schema.name,
      scope,
      origin: "schema_studio",
      success: true,
      durationMs,
      metadata: {
        type: schema.type,
        playerCount: schema.playerCount,
        completedView: durationMs >= 4000,
      },
    }).catch(() => {
      // Tracking best-effort
    });
  };

  const cancelCreate = () => {
    setView("review");
    setEditTarget(null);
  };

  // Extraire le numéro de position (P1, P2, ..., P6) du nom du schéma
  const getPositionNumber = (schemaName: string): number => {
    const match = schemaName.match(/P(\d)/);
    return match ? parseInt(match[1], 10) : 0;
  };

  // Obtenir les schémas du même type, triés dans l'ordre approprié
  const getSortedSchemasOfType = (type: SchemaType): Schema[] => {
    const filtered = schemas.filter((s) => s.type === type);
    if (type === "reception") {
      // Ordre pour réception: 1 → 6 → 5 → 4 → 3 → 2 → 1
      const positionOrder = [1, 6, 5, 4, 3, 2];
      const sorted = positionOrder
        .map((pos) => filtered.find((s) => getPositionNumber(s.name) === pos))
        .filter((s) => s !== undefined) as Schema[];
      return sorted;
    } else {
      // Ordre pour service: 1 → 2 → 3 → 4 → 5 → 6 → 1
      const sorted = filtered.sort((a, b) => getPositionNumber(a.name) - getPositionNumber(b.name));
      return sorted;
    }
  };

  // Naviguer vers le prochain schéma du même type
  const handleNextSchema = () => {
    if (!modalSchema) return;
    const sorted = getSortedSchemasOfType(modalSchema.type);
    if (sorted.length <= 1) return;
    const currentIndex = sorted.findIndex((s) => s.id === modalSchema.id);
    const nextIndex = (currentIndex + 1) % sorted.length;
    setModalSchema(sorted[nextIndex]);
  };

  // Obtenir le nom de la position suivante
  const getNextSchemaPosition = (): string | null => {
    if (!modalSchema) return null;
    const sorted = getSortedSchemasOfType(modalSchema.type);
    if (sorted.length <= 1) return null;
    const currentIndex = sorted.findIndex((s) => s.id === modalSchema.id);
    const nextIndex = (currentIndex + 1) % sorted.length;
    const nextSchema = sorted[nextIndex];
    const posMatch = nextSchema.name.match(/P(\d)/);
    return posMatch ? `P${posMatch[1]}` : null;
  };

  // Naviguer vers le schéma précédent du même type
  const handlePrevSchema = () => {
    if (!modalSchema) return;
    const sorted = getSortedSchemasOfType(modalSchema.type);
    if (sorted.length <= 1) return;
    const currentIndex = sorted.findIndex((s) => s.id === modalSchema.id);
    const prevIndex = (currentIndex - 1 + sorted.length) % sorted.length;
    setModalSchema(sorted[prevIndex]);
  };

  // Obtenir le nom de la position précédente
  const getPrevSchemaPosition = (): string | null => {
    if (!modalSchema) return null;
    const sorted = getSortedSchemasOfType(modalSchema.type);
    if (sorted.length <= 1) return null;
    const currentIndex = sorted.findIndex((s) => s.id === modalSchema.id);
    const prevIndex = (currentIndex - 1 + sorted.length) % sorted.length;
    const prevSchema = sorted[prevIndex];
    const posMatch = prevSchema.name.match(/P(\d)/);
    return posMatch ? `P${posMatch[1]}` : null;
  };

  const safeView: "review" | "create" | "edit" = !isAdmin && (view === "create" || view === "edit") ? "review" : view;

  const showSidebar = safeView === "review";

  if (studioAccess !== "enabled") {
    return (
      <div style={{ padding: 24, paddingBottom: 90, maxWidth: 560 }}>
        <h2 style={{ margin: "0 0 8px", fontWeight: 800 }}>Schémas</h2>
        {studioAccess === "disabled" && (
          <p style={{ fontSize: 15, lineHeight: 1.6 }}>
            📚 La révision des schémas est momentanément désactivée par le coach — concentre-toi sur le{" "}
            <b>Quiz Placement</b> pour t'entraîner !
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes bounce {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
        `}
      </style>
      <div
        style={
          {
            // CSS variables (used by some inline styles)
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            "--c-border": "#e5e7eb",
            "--c-surface2": "#f3f4f6",
            "--c-border2": "#d1d5db",
            "--c-accent2": "#3b82f6",
            "--c-muted": "#6b7280",
          } as React.CSSProperties
        }
      >
        <div
          style={{
            height: "calc(100vh - 140px)",
            minHeight: 560,
          display: "flex",
          flexDirection: "column",
          fontFamily: "system-ui, sans-serif",
          background: "#f3f4f6",
          overflow: "hidden",
          borderRadius: 12,
          margin: "0 12px 120px",
          boxShadow: "0 10px 30px rgba(0,0,0,.08)",
        }}
      >
        <div
          style={{
            background: "#1e3a5f",
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
            flexWrap: "wrap",
            minHeight: 48,
          }}
        >
          {showSidebar && (
            <button
              onClick={() => setSideVisible((v) => !v)}
              style={{
                padding: "6px 12px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,.25)",
                background: "rgba(255,255,255,.12)",
                color: "#fff",
                cursor: "pointer",
                display: isMobile ? "flex" : "none",
                alignItems: "center",
                gap: 7,
                flexShrink: 0,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 3.5, flexShrink: 0 }}>
                <div
                  style={{
                    width: 14,
                    height: 2,
                    background: "#fff",
                    borderRadius: 1,
                    transform: sideVisible ? "rotate(45deg) translate(0,5px)" : "none",
                    transition: "all .2s",
                  }}
                />
                <div style={{ width: 14, height: 2, background: "#fff", borderRadius: 1, opacity: sideVisible ? 0 : 1, transition: "all .2s" }} />
                <div
                  style={{
                    width: 14,
                    height: 2,
                    background: "#fff",
                    borderRadius: 1,
                    transform: sideVisible ? "rotate(-45deg) translate(0,-5px)" : "none",
                    transition: "all .2s",
                  }}
                />
              </div>
              <span style={{ opacity: sideVisible ? 0.6 : 1, transition: "opacity .2s" }}>{sideVisible ? "Masquer" : "Voir schémas"}</span>
            </button>
          )}

          <span style={{ fontSize: 20 }}>🏐</span>
          <div style={{ color: "#fff" }}>
            <div style={{ fontSize: 14, fontWeight: 700, display: isMobile ? "none" : "block" }}>Volley Schema Studio</div>
            <div style={{ fontSize: 9, color: "rgba(255,255,255,.4)", fontFamily: "monospace", display: "flex", gap: 5, alignItems: "center" }}>
              {schemas.length} schémas
              {isAdmin && <span style={{ background: "#f59e0b", color: "#000", borderRadius: 4, padding: "0 4px", fontSize: 8, fontWeight: 700 }}>ADMIN</span>}
            </div>
          </div>

          <div style={{ display: "flex", gap: 2, background: "rgba(255,255,255,.1)", borderRadius: 8, padding: 3, marginLeft: 4 }}>
            {([
              ["6", "6 joueuses"],
              ["4", "M13"],
            ] as const).map(([v, l]) => (
              <button
                key={v}
                onClick={() => {
                  setTab(v);
                  setView("review");
                  setActive(null);
                }}
                style={{
                  padding: "5px 10px",
                  borderRadius: 6,
                  border: "none",
                  background: tab === v && safeView === "review" ? "#fff" : "transparent",
                  color: tab === v && safeView === "review" ? "#1e3a5f" : "rgba(255,255,255,.7)",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "all .15s",
                }}
              >
                {l}
              </button>
            ))}
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 5, alignItems: "center", flexShrink: 0 }}>
            {isAdmin && (
              <button
                onClick={saveToDb}
                title={hasUnsavedChanges ? "⚠️ Modifications non enregistrées - Cliquez pour sauvegarder" : "Enregistrer en base"}
                disabled={dbSaving}
                style={{
                  padding: hasUnsavedChanges ? "10px 16px" : "6px 10px",
                  borderRadius: hasUnsavedChanges ? 10 : 7,
                  border: hasUnsavedChanges ? "none" : "1px solid rgba(255,255,255,.22)",
                  background: dbSaving ? "#94a3b8" : hasUnsavedChanges ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" : "rgba(255,255,255,.1)",
                  color: hasUnsavedChanges || dbSaving ? "#fff" : "rgba(255,255,255,.85)",
                  fontSize: hasUnsavedChanges ? 13 : 11,
                  fontWeight: hasUnsavedChanges ? 900 : 600,
                  cursor: dbSaving ? "not-allowed" : "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  gap: hasUnsavedChanges ? 5 : 3,
                  boxShadow: hasUnsavedChanges && !dbSaving ? "0 4px 12px rgba(16,185,129,.5), 0 0 20px rgba(16,185,129,.3)" : "none",
                  transition: "all .2s",
                  animation: hasUnsavedChanges && !dbSaving ? "bounce 0.6s infinite" : "none",
                }}
                onMouseEnter={(e) => {
                  if (!dbSaving && hasUnsavedChanges) {
                    e.currentTarget.style.transform = "scale(1.1)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(16,185,129,.6), 0 0 25px rgba(16,185,129,.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!dbSaving && hasUnsavedChanges) {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(16,185,129,.5), 0 0 20px rgba(16,185,129,.3)";
                  }
                }}
              >
                <span style={{ fontSize: hasUnsavedChanges ? 16 : 14 }}>{hasUnsavedChanges ? "💾" : "💾"}</span>
                {!isMobile && <span>{dbSaving ? "Sauve…" : "Enregistrer"}</span>}
              </button>
            )}

            {isAdmin && (
              <button
                onClick={() => exportJSON(schemas)}
                title="Exporter JSON"
                style={{
                  padding: "6px 10px",
                  borderRadius: 7,
                  border: "1px solid rgba(255,255,255,.22)",
                  background: "rgba(255,255,255,.1)",
                  color: "rgba(255,255,255,.85)",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <span>⬇️</span>
                {!isMobile && <span>Export</span>}
              </button>
            )}

            {isAdmin && (
              <label
                style={{
                  padding: "6px 10px",
                  borderRadius: 7,
                  border: "1px solid rgba(255,255,255,.22)",
                  background: "rgba(255,255,255,.1)",
                  color: "rgba(255,255,255,.85)",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <span>⬆️</span>
                {!isMobile && <span>Import</span>}
                <input
                  type="file"
                  accept=".json"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;

                    importJSON(f, (data) => {
                      const next = normalizeSchemas(data);
                      persist(next);
                      alert(`${next.length} schémas importés !`);
                    });

                    e.target.value = "";
                  }}
                />
              </label>
            )}

            {isAdmin && safeView === "review" && (
              <button
                onClick={() => {
                  setView("create");
                  setEditTarget(null);
                }}
                style={{
                  padding: "7px 12px",
                  borderRadius: 8,
                  border: "none",
                  background: "#3b82f6",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  boxShadow: "0 2px 6px rgba(59,130,246,.3)",
                  whiteSpace: "nowrap",
                }}
              >
                + Nouveau
              </button>
            )}

            {isAdmin && (safeView === "create" || safeView === "edit") && (
              <button
                onClick={cancelCreate}
                style={{
                  padding: "7px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,.25)",
                  background: "transparent",
                  color: "rgba(255,255,255,.8)",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {showSidebar && (
            <>
              <div
                onClick={() => setSideVisible(false)}
                style={{
                  display: isMobile ? "block" : "none",
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,.6)",
                  zIndex: 40,
                  backdropFilter: "blur(2px)",
                  opacity: sideVisible ? 1 : 0,
                  pointerEvents: sideVisible ? "auto" : "none",
                  transition: "opacity .25s",
                }}
              />

              {!sideVisible && (
                <div
                  style={{
                    width: 28,
                    flexShrink: 0,
                    background: "#fff",
                    borderRight: "1px solid #e5e7eb",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    paddingTop: 10,
                    gap: 6,
                  }}
                >
                  <button
                    onClick={() => setSideVisible(true)}
                    title="Afficher"
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      border: "1px solid #e5e7eb",
                      background: "#f3f4f6",
                      cursor: "pointer",
                      fontSize: 13,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      color: "#6b7280",
                    }}
                  >
                    ›
                  </button>
                  <div
                    style={{
                      fontSize: 8,
                      color: "#9ca3af",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      writingMode: "vertical-rl",
                      transform: "rotate(180deg)",
                      marginTop: 6,
                    }}
                  >
                    {tabSchemas.length} schémas
                  </div>
                </div>
              )}

              <div
                style={{
                  ...(isMobile
                    ? {
                        position: "fixed" as const,
                        top: 0,
                        left: 0,
                        bottom: 0,
                        zIndex: 50,
                        transform: sideVisible ? "translateX(0)" : "translateX(-100%)",
                        width: Math.min(sideW, window.innerWidth * 0.82),
                      }
                    : {
                        width: sideVisible ? sideW : 0,
                        overflow: "hidden",
                        flexShrink: 0,
                        transition: "width .28s cubic-bezier(.4,0,.2,1)",
                      }),
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: isMobile && sideVisible ? "4px 0 20px rgba(0,0,0,.18)" : "none",
                }}
              >
                <div
                  style={{
                    padding: "12px 10px 10px 14px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: isMobile ? ("wrap" as const) : ("nowrap" as const),
                    rowGap: isMobile ? 8 : 0,
                    borderBottom: "1px solid #e5e7eb",
                    minWidth: 0,
                    background: "#f3f4f6",
                  }}
                >
                  <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "#111827" }}>
                      {tab === "4" ? "M13 — 4 joueuses" : "Seniors — 6 joueuses"}
                    </div>
                    <div style={{ fontSize: 9, color: "#6b7280", fontFamily: "monospace", marginTop: 2 }}>
                      {tabSchemas.length} schéma{tabSchemas.length > 1 ? "s" : ""}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexShrink: 0,
                      marginLeft: "auto",
                      width: isMobile ? "100%" : "auto",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      onClick={() => void refreshSchemas()}
                      title="Recharger les schémas depuis la base"
                      disabled={schemasLoading}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: "1px solid #d1d5db",
                        background: schemasLoading ? "#f3f4f6" : "#fff",
                        cursor: schemasLoading ? "not-allowed" : "pointer",
                        fontSize: 14,
                        fontWeight: 900,
                        color: schemasLoading ? "#9ca3af" : "#374151",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        whiteSpace: "nowrap",
                        transition: "all .15s",
                      }}
                    >
                      <span style={{ display: "inline-block", animation: schemasLoading ? "spin 1s linear infinite" : "none" }}>↻</span>
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => setTacticsOpen(true)}
                        title="Gérer les tactiques"
                        style={{
                          padding: "6px 10px",
                          borderRadius: 8,
                          border: "1px solid #bfdbfe",
                          background: "#eff6ff",
                          cursor: "pointer",
                          fontSize: 11,
                          fontWeight: 900,
                          color: "#2563eb",
                          whiteSpace: "nowrap",
                        }}
                      >
                        📁 Tactiques
                      </button>
                    )}
                    <button
                      onClick={() => setSideVisible(false)}
                      title="Fermer"
                      style={{
                        fontSize: 14,
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      ‹
                    </button>
                  </div>
                </div>

                <div style={{ flex: 1, overflowY: "auto", padding: "4px 8px 12px", display: "flex", flexDirection: "column", gap: 5, minWidth: 200 }}>
                  <div style={{ padding: "6px 2px 2px" }}>
                    <select
                      value={tacticFilter}
                      onChange={(e) => setTacticFilter(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        borderRadius: 10,
                        border: "1px solid #e5e7eb",
                        background: "#fff",
                        color: "#111827",
                        fontSize: 12,
                        fontFamily: "inherit",
                        fontWeight: 800,
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                      title="Filtrer par tactique"
                    >
                      <option value="">Toutes les tactiques</option>
                      <option value="__none__">Sans tactique</option>
                      {tactics
                        .slice()
                        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr"))
                        .map((t) => {
                          const id = Number(t.id);
                          const c = Number.isFinite(id) ? tacticCountsById.get(id) || 0 : 0;
                          return (
                            <option key={t.id} value={String(t.id)}>
                              {t.name} ({c})
                            </option>
                          );
                        })}
                    </select>
                  </div>

                  {tabSchemas.length === 0 ? (
                    <div style={{ padding: "24px 16px", textAlign: "center", color: "#6b7280", fontSize: 12, lineHeight: 1.6 }}>
                      <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.5 }}>📋</div>
                      Aucun schéma.
                      <br />
                      {isAdmin ? (
                        <span
                          style={{ color: "#2563eb", fontWeight: 700, cursor: "pointer" }}
                          onClick={() => {
                            setView("create");
                            setEditTarget(null);
                          }}
                        >
                          + Créer maintenant
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    filteredTabSchemas.map((s) => (
                      <SideCard
                        key={s.id}
                        schema={s}
                        tacticLabel={getTacticLabel(s)}
                        active={active?.id === s.id}
                        onClick={handleSelect}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onDuplicate={handleDuplicate}
                        onPlay={handlePlay}
                        isAdmin={isAdmin}
                      />
                    ))
                  )}
                </div>

                {!isMobile && (
                  <div
                    onMouseDown={onSideMouseDown}
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: 5,
                      cursor: "col-resize",
                      background: "transparent",
                      borderRight: "1px solid #e5e7eb",
                      zIndex: 10,
                      transition: "background .15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#bfdbfe";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", display: "flex", flexDirection: "column", gap: 3, pointerEvents: "none" }}>
                      {[0, 1, 2].map((i) => (
                        <div key={i} style={{ width: 2, height: 2, borderRadius: "50%", background: "#9ca3af" }} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div style={{ flex: 1, overflowY: "auto", background: "#f3f4f6" }}>
            {safeView === "review" && (
              <ReviewPanel
                schemas={filteredTabSchemas}
                tacticLabel={getTacticLabel}
                onSchemaClick={handleSelect}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
                isAdmin={isAdmin}
              />
            )}
            {safeView === "create" && isAdmin && <CreatorPanel initial={null} onSave={handleSave} onCancel={cancelCreate} tactics={tactics} />}
            {safeView === "edit" && isAdmin && editTarget && <CreatorPanel initial={editTarget} onSave={handleSave} onCancel={cancelCreate} tactics={tactics} />}
          </div>
        </div>
      </div>
      </div>

      {isAdmin && (
        <TacticsModal
          open={tacticsOpen}
          onClose={() => setTacticsOpen(false)}
          scope={scope}
          tactics={tactics}
          setTactics={setTactics}
          schemas={schemas}
          persist={persist}
        />
      )}

      {modalSchema && (
        <SchemaModal
          schema={modalSchema}
          myRole={myRole}
          onClose={handleCloseModal}
          onPlayStart={(info) => handleModalPlayStart(modalSchema, info)}
          onNext={handleNextSchema}
          onPrev={handlePrevSchema}
          nextSchemaPosition={getNextSchemaPosition()}
          prevSchemaPosition={getPrevSchemaPosition()}
        />
      )}
    </>
  );
}
